import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchProtectedFile, getFileFetchUrl } from "../utils/fileUrlHelper";
import AvatarImg from "../components/AvatarImg";
import SecureMediaViewer from "../components/SecureMediaViewer";
import MediaActionIcon, { MediaActionLoading } from "../components/MediaActionIcon";
import { readSensitiveItem, writeSensitiveItem } from "../utils/sensitiveSessionStorage";
import AsyncActionButton from "../components/AsyncActionButton";
import FormValidationNotice, { useFormValidation } from "../components/FormValidationNotice";
import RenewableFileImage from "../components/RenewableFileImage";
import { useLanguage } from "../i18n/LanguageContext";
import OzelTakvimim from "./OzelTakvimim";
import "../styles/pages/cycle.css";
import {
  saglikKayitlariGetirService,
  saglikKaydiEkleService,
  saglikKaydiGuncelleService,
  saglikKaydiSilService,
  kendiSaglikProfiliniGuncelleService,
} from "../services/healthService";
import { createActivity, getActivityActor } from "../services/activityService";
import { appAlert, appConfirm } from "../components/AppDialog";
import { canManageChildMember, memberUid } from "../utils/familyMemberAccess";
import { isAndroidPlatform, isIOSPlatform } from "../utils/platformFeatures";
import useHealthKeyboardViewport from "../hooks/useHealthKeyboardViewport";
import { androidDosyaPaylas } from "../utils/androidFileShare";
import {
  getHealthKitStatus,
  openHealthKitSettings,
  readHealthKitSummary,
  requestHealthKitAuthorization,
  subscribeHealthKitRefresh,
} from "../services/healthKitService";
import {
  HEALTH_TEXT_LIMITS,
  healthTextLimitForField,
  limitHealthText,
} from "../utils/healthTextLimits";
import healthSummaryAsset from "../assets/saglik_saglik-ozeti.png";
import healthMedicineAsset from "../assets/saglik_ilac-takibi.png";
import healthAppointmentAsset from "../assets/saglik_randevular.png";
import healthMeasurementAsset from "../assets/saglik_olcumler.png";
import healthReportAsset from "../assets/saglik_tahlil-raporlar.png";
import healthAllergyAsset from "../assets/saglik_alerjiler.png";
import healthConditionAsset from "../assets/saglik_kronik-hastaliklar.png";
import healthVaccineAsset from "../assets/saglik_asi-takibi.png";
import healthEmergencyAsset from "../assets/saglik_acil-durum-karti.png";
import healthNoteAsset from "../assets/saglik_saglik-notlari.png";
import healthPrivateCalendarAsset from "../assets/saglik_ozel-takvimim.png";
import healthStepsAsset from "../assets/saglik_adim.png";
import healthEnergyAsset from "../assets/saglik_enerji.png";
import healthDistanceAsset from "../assets/saglik_mesafe.png";

const ROL_SIRALAMASI = ["Kişisel", "Baba", "Anne", "Çocuk", "Amca", "Hala", "Teyze", "Dayı", "Dede", "Nene", "Diğer"];

const uyelerSirala = (liste) =>
  [...liste].sort((a, b) => {
    const ai = ROL_SIRALAMASI.indexOf(a.rol);
    const bi = ROL_SIRALAMASI.indexOf(b.rol);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

const bugunKey = () => new Date().toISOString().slice(0, 10);
const simdikiSaatKey = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
};

const getRecordSortTime = (record) => {
  const candidates = record?.type === "measurement"
    ? [record?.date, record?.updatedAt, record?.updated_at, record?.createdAt, record?.created_at]
    : [record?.createdAt, record?.created_at, record?.updatedAt, record?.updated_at, record?.date, record?.plannedDate];
  for (const value of candidates) {
    if (!value) continue;
    const time = new Date(value).getTime();
    if (!Number.isNaN(time)) return time;
  }
  return 0;
};

const sortRecordsNewestFirst = (list = []) =>
  [...list].sort((a, b) => getRecordSortTime(b) - getRecordSortTime(a));


const HEALTH_RECORDS_CACHE_PREFIX = "familyPlanner.healthRecords.";
const healthRecordsMemoryCache = new Map();

const getHealthRecordsCacheKey = (familyId) => `${HEALTH_RECORDS_CACHE_PREFIX}${String(familyId || "")}`;

const readCachedHealthRecords = (familyId) => {
  if (!familyId) return [];
  const key = getHealthRecordsCacheKey(familyId);
  if (healthRecordsMemoryCache.has(key)) return healthRecordsMemoryCache.get(key);
  try {
    // Önce localStorage (kill+restart'ta da kalır), yoksa sessionStorage'a bak
    const raw = readSensitiveItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed : [];
    healthRecordsMemoryCache.set(key, list);
    return list;
  } catch {
    return [];
  }
};

const writeCachedHealthRecords = (familyId, list) => {
  if (!familyId || !Array.isArray(list)) return;
  const key = getHealthRecordsCacheKey(familyId);
  healthRecordsMemoryCache.set(key, list);
  try {
    writeSensitiveItem(key, JSON.stringify(list));
  } catch {
    // Cache sadece ekrandaki anlık git-gel etkisini önlemek içindir. Yazılamazsa veri akışı bozulmaz.
  }
};


const getDocumentSortTime = (doc) => {
  const candidates = [doc?.eklenmeTarihi, doc?.createdAt, doc?.created_at, doc?.updatedAt, doc?.updated_at, doc?.tarih];
  for (const value of candidates) {
    if (!value) continue;
    const date = value?.toDate ? value.toDate() : new Date(value);
    const time = date?.getTime?.();
    if (!Number.isNaN(time)) return time;
  }
  return 0;
};

const sortDocumentsNewestFirst = (list = []) =>
  [...list].sort((a, b) => getDocumentSortTime(b) - getDocumentSortTime(a));

const normalizeDosyaTipi = (tip) => {
  const t = String(tip || "").toLowerCase().trim();
  const mimeMap = {
    "image/jpeg": "jpg", "image/jpg": "jpg", "image/png": "png",
    "image/gif": "gif", "image/webp": "webp", "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  };
  return mimeMap[t] || t.split("/").pop() || t;
};

const dosyaIkonu = (tip) => {
  const tip2 = normalizeDosyaTipi(tip);
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(tip2)) return "🖼️";
  if (tip2 === "pdf") return "📄";
  if (["doc", "docx"].includes(tip2)) return "📝";
  if (["xls", "xlsx"].includes(tip2)) return "📊";
  return "📎";
};

const boyutFormatla = (b) => {
  const n = Number(b || 0);
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const safeDosyaAdi = (dosyaAdi, mimeType, fallback = "belge") => {
  const clean = String(dosyaAdi || fallback).replace(/[\\/:*?"<>|]+/g, "_");
  if (clean.includes(".")) return clean;
  const ext = normalizeDosyaTipi(mimeType);
  return ext ? `${clean}.${ext === "jpg" ? "jpg" : ext}` : clean;
};

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const fetchBelgeBlob = async (url) => {
  const fileUrl = getFileFetchUrl(url);
  if (!fileUrl) throw new Error("Dosya URL bulunamadı.");
  const res = await fetchProtectedFile(fileUrl, { method: "GET", cache: "no-store" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.blob();
};

const normalizeMedicineTimes = (value = "") => {
  const list = Array.isArray(value) ? value : String(value || "").split(",");

  return Array.from(new Set(
    list
      .map((x) => String(x || "").trim())
      .map((x) => {
        const match = x.match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return null;
        const hour = Number(match[1]);
        const minute = Number(match[2]);
        if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
        return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
      })
      .filter(Boolean)
  )).sort();
};

const getDoseLog = (medicine, dateKey, time) => {
  const doseLog = medicine?.doseLogs?.[dateKey]?.[time];
  if (!doseLog && medicine?.takenDates?.[dateKey] === true) return { status: "taken" };
  if (!doseLog) return { status: "pending" };
  if (typeof doseLog === "string") return { status: doseLog };
  return { status: doseLog.status || "pending", ...doseLog };
};

const getDoseDate = (time, baseDate = new Date()) => {
  const [hour, minute] = String(time || "00:00").split(":").map(Number);
  const d = new Date(baseDate);
  d.setHours(Number.isFinite(hour) ? hour : 0, Number.isFinite(minute) ? minute : 0, 0, 0);
  return d;
};

const medicineDateAllowed = (medicine, dateKey) => {
  if (!medicine) return false;
  if (medicine.startDate && dateKey < medicine.startDate) return false;
  if (medicine.endDate && dateKey > medicine.endDate) return false;
  return true;
};

const doseStatusInfo = (medicine, time, dateKey = bugunKey(), lang = "tr") => {
  const log = getDoseLog(medicine, dateKey, time);
  const now = new Date();
  const doseDate = getDoseDate(time, now);
  const diffMin = Math.round((doseDate.getTime() - now.getTime()) / 60000);
  const locale = lang === "tr" ? "tr-TR" : "en-US";

  if (log.status === "taken") return { status: "taken", label: "✅", icon: "✅", className: "taken" };
  if (log.status === "skipped") return { status: "skipped", label: "⏭️", icon: "⏭️", className: "skipped" };
  if (log.status === "snoozed") {
    const snoozeDate = log.snoozeUntil ? new Date(log.snoozeUntil) : null;
    const snoozeText = snoozeDate && !Number.isNaN(snoozeDate.getTime())
      ? snoozeDate.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
      : "⏰";
    return { status: "snoozed", label: `${snoozeText} ⏰`, icon: "⏰", className: "snoozed" };
  }
  if (diffMin < -30) return { status: "missed", label: "⚠️", icon: "⚠️", className: "missed" };
  if (diffMin <= 0) return { status: "due", label: "🔔", icon: "🔔", className: "due" };
  if (diffMin <= 60) return { status: "upcoming", label: `${diffMin}'`, icon: "🕒", className: "upcoming" };
  return { status: "pending", label: "○", icon: "○", className: "pending" };
};

const getMedicineTodayStats = (medicine) => {
  const today = bugunKey();
  const times = normalizeMedicineTimes(medicine?.times || []);
  if (!medicineDateAllowed(medicine, today)) {
    return { total: 0, taken: 0, pending: 0, missed: 0, nextTime: null, progress: 0 };
  }

  let taken = 0;
  let pending = 0;
  let missed = 0;
  let nextTime = null;

  times.forEach((time) => {
    const info = doseStatusInfo(medicine, time, today);
    if (info.status === "taken") taken += 1;
    else if (["missed", "due"].includes(info.status)) missed += 1;
    else pending += 1;

    if (!nextTime && ["pending", "upcoming", "due", "snoozed"].includes(info.status)) {
      nextTime = time;
    }
  });

  return {
    total: times.length,
    taken,
    pending,
    missed,
    nextTime,
    progress: times.length ? Math.round((taken / times.length) * 100) : 0,
  };
};


const HEALTH_LABELS = {
  "İlaç adı":"Medicine name", "Kayıt adı":"Record name", "Doz":"Dose", "Saatler":"Times", "Büyük tansiyon":"Systolic", "Küçük tansiyon":"Diastolic",
  "Aile Hekimi":"Family Doctor", "Dahiliye":"Internal Medicine", "Kardiyoloji":"Cardiology", "Ortopedi":"Orthopedics", "Göz":"Eye", "Diş":"Dental", "Kulak Burun Boğaz":"ENT", "Çocuk Sağlığı":"Pediatrics", "Kadın Doğum":"Gynecology", "Nöroloji":"Neurology", "Psikiyatri":"Psychiatry", "Dermatoloji":"Dermatology", "Fizik Tedavi":"Physical Therapy", "Üroloji":"Urology", "Genel Cerrahi":"General Surgery", "Radyoloji":"Radiology", "Laboratuvar":"Laboratory",
  "Muayene":"Examination", "Kontrol":"Check-up", "Tahlil":"Lab Test", "Tetkik":"Medical Test", "Aşı":"Vaccine", "Acil":"Emergency", "Online Görüşme":"Online Consultation",
  "Tansiyon":"Blood Pressure", "Kan Şekeri":"Blood Sugar", "Nabız":"Pulse", "Kilo":"Weight", "Boy":"Height", "Ateş":"Fever", "Kolesterol":"Cholesterol", "Kreatinin":"Creatinine",
  "Kan Tahlili":"Blood Test", "İdrar Tahlili":"Urine Test", "BT / Tomografi":"CT / Tomography", "Ultrason":"Ultrasound", "Röntgen":"X-ray", "Patoloji":"Pathology", "Epikriz":"Discharge Summary", "Reçete":"Prescription", "Doktor Raporu":"Doctor Report", "Diğer":"Other",
  "Polen":"Pollen", "Ev tozu akarı":"Dust mite", "Kedi tüyü":"Cat hair", "Köpek tüyü":"Dog hair", "Fıstık":"Peanut", "Ceviz":"Walnut", "Süt":"Milk", "Yumurta":"Egg", "Deniz ürünleri":"Seafood", "Arı zehiri":"Bee venom", "Mantar":"Mushroom",
  "Gıda":"Food", "Çevresel":"Environmental", "Hayvan":"Animal", "Böcek":"Insect", "Kimyasal":"Chemical", "Hafif":"Mild", "Orta":"Moderate", "Ciddi":"Severe",
  "Diyabet (Tip 2)":"Diabetes (Type 2)", "Diyabet (Tip 1)":"Diabetes (Type 1)", "Hipertansiyon":"Hypertension", "Astım":"Asthma", "Kalp yetmezliği":"Heart failure", "Koroner arter hastalığı":"Coronary artery disease", "Böbrek yetmezliği":"Kidney failure", "Romatoid artrit":"Rheumatoid arthritis", "Tiroid hastalığı":"Thyroid disease", "Depresyon":"Depression", "Anksiyete bozukluğu":"Anxiety disorder",
  "Grip":"Flu", "Tetanoz":"Tetanus", "Hepatit B":"Hepatitis B", "Hepatit A":"Hepatitis A", "Kızamık":"Measles", "Kızamıkçık":"Rubella", "Kabakulak":"Mumps", "Suçiçeği":"Chickenpox", "Menenjit":"Meningitis",
  "Genel":"General", "Doktor Notu":"Doctor Note", "Beslenme":"Nutrition", "Ruh Hali":"Mood", "Egzersiz":"Exercise", "Uyku":"Sleep", "Tedavi":"Treatment",
  "İlaç adı · Örn: Parol":"Medicine name · E.g. Paracetamol", "Doz · Örn: 500 mg":"Dose · E.g. 500 mg", "Saatler · 08:00, 14:00, 20:00":"Times · 08:00, 14:00, 20:00", "Not · Tok karna, yemek sonrası, doktor notu...":"Note · Full stomach, after meal, doctor note...",
  "Doktor / Hastane":"Doctor / Hospital", "Branş seç veya yaz":"Select or type specialty", "Konum / hastane adresi":"Location / hospital address", "Büyük · 120":"Systolic · 120", "Küçük · 80":"Diastolic · 80", "Değer":"Value", "Birim · mg/dL, kg, °C":"Unit · mg/dL, kg, °C", "Durum seç":"Select status", "Tür seç":"Select type", "Şiddet seç":"Select severity", "Belirti · kaşıntı, nefes darlığı...":"Symptoms · itching, shortness of breath...", "Acil durumda yapılacak":"What to do in emergency", "Kaçınılacak şeyler / not":"Things to avoid / note",
  "Doktor / klinik":"Doctor / clinic", "Takip sıklığı · 6 ayda bir":"Follow-up frequency · every 6 months", "Kullanılan ilaçlar / not":"Medicines used / note", "Doz no · 1. doz":"Dose no · 1st dose", "Kan grubu":"Blood type", "Acil kişi":"Emergency contact", "Yakınlık":"Relationship", "Telefon":"Phone", "Kritik alerjiler":"Critical allergies", "Kronik hastalıklar / kritik ilaçlar":"Chronic diseases / critical medicines", "Başlık":"Title", "Kategori":"Category", "Sağlık notu":"Health note", "Tahlil / rapor adı":"Test / report name", "Rapor tipi":"Report type", "Kurum / laboratuvar":"Institution / laboratory", "Doktor":"Doctor", "Sonuç özeti":"Result summary", "Dosya seçmek için dokun":"Tap to choose a file", "Planlanan aşı tarihi":"Planned vaccine date", "Yapıldığı tarih":"Completed date", "Sonraki doz tarihi":"Next dose date",
  "Sağlık Merkezi":"Health Center", "kayıt":"records", "Başlangıç tarihi":"Start date", "Bitiş tarihi":"End date", "Randevu tarihi":"Appointment date", "Randevu saati":"Appointment time",
  "Not":"Note",
  "Düzenle":"Edit", "Sil":"Delete", "Ara":"Call", "Bugün":"Today", "Aldım":"Taken", "Bu bölümde henüz kayıt yok.":"No records in this section yet.", "Sağlık Belgesi":"Health Document", "Belge Kasası":"Document Vault", "Aile Sağlık Kartları":"Family Health Cards", "Sağlık Kaydı":"Health Record", "Kayıt":"Record", "İlaç":"Medicine", "sağlık kaydı":"health records", "Kaydediliyor...":"Saving...", "Ekleniyor...":"Adding...", "Kaydet":"Save", "İptal":"Cancel", "Atla":"Skip", "doz":"doses", "Ben":"Me", "Bildirimler aktif edildi.":"Notifications enabled.", "Bildirim aktif edilemedi.":"Notifications could not be enabled.", "Kritik alerji":"Critical allergy", "Doktor / Hastane belirtilmedi":"Doctor / hospital not specified", "Saat":"Time", "Normal":"Normal", "Yok":"None", "yok":"none", "Sağlık":"Health", "Baba":"Father", "Anne":"Mother", "Çocuk":"Child", "Dede":"Grandfather", "Nene":"Grandmother", "Amca":"Uncle", "Teyze":"Aunt", "Dayı":"Uncle", "Erkek":"Male", "Kadın":"Female", "Kız":"Girl", "Belirtilmedi":"Not specified", "Cinsiyet belirtilmedi":"Gender not specified", "gir":"enter", "Randevu":"Appointment", "Alerjiler":"Allergies" , "KOAH":"COPD", "Epilepsi":"Epilepsy", "Parkinson":"Parkinson's", "Multiple Skleroz":"Multiple Sclerosis", "Karaciğer hastalığı":"Liver disease", "Psöriazis":"Psoriasis", "Bipolar bozukluk":"Bipolar disorder", "Trigliserit":"Triglyceride", "Penisilin":"Penicillin", "Aspirin":"Aspirin", "İbuprofen":"Ibuprofen", "Lateks":"Latex", "Pnömokok":"Pneumococcal", "Rota":"Rotavirus", "MR":"MRI", "EKG":"ECG", "EKO":"Echo", "Gluten":"Gluten", "COVID-19":"COVID-19", "HPV":"HPV", "BCG":"BCG", "DaBT-IPA-Hib":"DTaP-IPV-Hib", "KPA":"PCV"
};
const hl = (lang, text) => (lang === "en" ? (HEALTH_LABELS[text] || text) : text);
const healthCanonical = (text) => {
  const raw = String(text || "");
  const found = Object.entries(HEALTH_LABELS).find(([, en]) => en.toLowerCase() === raw.toLowerCase());
  return found ? found[0] : raw;
};

// HEALTH_CARDS: component içinde t() ile oluşturulur
const HEALTH_CARD_IDS = [
  { id: "summary",     icon: healthSummaryAsset,     color: "#ef476f" },
  { id: "medicine",    icon: healthMedicineAsset,    color: "#22c55e" },
  { id: "appointment", icon: healthAppointmentAsset, color: "#8b5cf6" },
  { id: "measurement", icon: healthMeasurementAsset, color: "#06b6d4" },
  { id: "report",      icon: healthReportAsset,      color: "#f59e0b" },
  { id: "allergy",     icon: healthAllergyAsset,     color: "#ef4444" },
  { id: "condition",   icon: healthConditionAsset,   color: "#ec4899" },
  { id: "vaccine",     icon: healthVaccineAsset,     color: "#14b8a6" },
  { id: "emergency",   icon: healthEmergencyAsset,   color: "#dc2626" },
  { id: "note",        icon: healthNoteAsset,        color: "#64748b" },
];


const BRANCH_OPTIONS = ["Aile Hekimi","Dahiliye","Kardiyoloji","Ortopedi","Göz","Diş","Kulak Burun Boğaz","Çocuk Sağlığı","Kadın Doğum","Nöroloji","Psikiyatri","Dermatoloji","Fizik Tedavi","Üroloji","Genel Cerrahi","Radyoloji","Laboratuvar"];
const APPOINTMENT_TYPES = ["Muayene","Kontrol","Tahlil","Tetkik","Diş","Aşı","Fizik Tedavi","Acil","Online Görüşme"];
const MEASUREMENT_OPTIONS = ["Tansiyon","Kan Şekeri","Nabız","Kilo","Boy","Ateş","SpO2","Kolesterol","Trigliserit","HbA1c","Kreatinin"];
const BODY_MEASUREMENT_UNITS = { Boy: "cm", Kilo: "kg" };
const bodyMeasurementUnit = (measurementType) => BODY_MEASUREMENT_UNITS[healthCanonical(measurementType)] || "";
const normalizeBodyMeasurementInput = (measurementType, value) => {
  const unit = bodyMeasurementUnit(measurementType);
  if (!unit) return String(value || "").trim();
  const match = String(value || "").trim().replace(/,/g, ".").match(/^-?\d+(?:\.\d+)?/);
  if (!match) return "";
  const number = Number(match[0]);
  return Number.isFinite(number) ? String(Number(number.toFixed(2))) : "";
};
const REPORT_TYPE_OPTIONS = ["Kan Tahlili","İdrar Tahlili","MR","BT / Tomografi","Ultrason","Röntgen","EKG","EKO","Patoloji","Epikriz","Reçete","Doktor Raporu","Diğer"];
const ALLERGY_OPTIONS = ["Polen","Ev tozu akarı","Kedi tüyü","Köpek tüyü","Fıstık","Ceviz","Süt","Yumurta","Gluten","Deniz ürünleri","Penisilin","Aspirin","İbuprofen","Lateks","Arı zehiri","Mantar"];
const ALLERGY_TYPES = ["İlaç","Gıda","Çevresel","Hayvan","Böcek","Kimyasal","Diğer"];
const CONDITION_OPTIONS = ["Diyabet (Tip 2)","Diyabet (Tip 1)","Hipertansiyon","Astım","KOAH","Kalp yetmezliği","Koroner arter hastalığı","Epilepsi","Parkinson","Multiple Skleroz","Tiroid hastalığı","Böbrek yetmezliği","Karaciğer hastalığı","Romatoid artrit","Psöriazis","Depresyon","Anksiyete bozukluğu","Bipolar bozukluk"];
const VACCINE_OPTIONS = ["Grip","COVID-19","Tetanoz","Hepatit B","Hepatit A","Kızamık","Kızamıkçık","Kabakulak","Suçiçeği","Pnömokok","Menenjit","HPV","BCG","DaBT-IPA-Hib","KPA","Rota"];
const NOTE_CATEGORY_OPTIONS = ["Genel","Doktor Notu","Beslenme","Ruh Hali","Egzersiz","Uyku","Tedavi","Kontrol","Diğer"];

const formatDateTR = (value, lang = "tr") => {
  if (!value) return "-";
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(lang === "en" ? "en-US" : "tr-TR", { day: "2-digit", month: "short", year: "numeric" });
};

const sortByDateAsc = (items, key = "date") =>
  [...items].sort((a, b) => String(a?.[key] || "9999-99-99").localeCompare(String(b?.[key] || "9999-99-99")));

const recordDetailRows = (record, t, lang = "tr") => {
  const _t = (k) => (typeof t === "function" ? t(k) : k);
  const displayDate = (value) => (value ? formatDateTR(value, lang) : "");
  if (!record) return [];
  if (record.type === "appointment") {
    return [
      [_t("health.lbl.branch"), record.branch],
      [_t("health.lbl.type"), record.appointmentType],
      [_t("health.lbl.location"), record.location],
      [_t("health.lbl.date"), `${displayDate(record.date) || "-"}${record.time ? ` · ${record.time}` : ""}`],
      [_t("health.lbl.note"), record.note],
    ].filter(([, v]) => v);
  }
  if (record.type === "measurement") {
    const valueText = ["Tansiyon", "Blood Pressure"].includes(record.measurementType)
      ? `${record.systolic || "-"}/${record.diastolic || "-"} mmHg`
      : `${record.value || "-"}${record.unit ? ` ${record.unit}` : ""}`;
    return [
      [_t("health.lbl.measureType"), record.measurementType],
      [_t("health.lbl.value"), valueText],
      [_t("health.lbl.context"), record.context],
      [_t("health.lbl.date"), displayDate(record.date)],
      [_t("health.lbl.note"), record.note],
    ].filter(([, v]) => v);
  }
  if (record.type === "allergy") {
    return [[_t("health.lbl.allergyType"), record.allergyType], [_t("health.lbl.severity"), record.level], [_t("health.lbl.symptoms"), record.symptoms], [_t("health.lbl.action"), record.action], [_t("health.lbl.avoid"), record.avoid], [_t("health.lbl.note"), record.note]].filter(([, v]) => v);
  }
  if (record.type === "condition") {
    return [[_t("health.lbl.diagnosisDate"), displayDate(record.diagnosisDate)], [_t("health.lbl.doctor"), record.doctor], [_t("health.lbl.followUp"), record.followUp], [_t("health.lbl.medicines"), record.medicines], [_t("health.lbl.note"), record.note]].filter(([, v]) => v);
  }
  if (record.type === "vaccine") {
    return [[_t("health.dose.label"), record.doseNo], [_t("health.dose.plan"), displayDate(record.plannedDate)], [_t("health.dose.done"), record.done ? (displayDate(record.doneDate) || _t("health.dose.yes")) : _t("health.dose.no")], [_t("health.dose.next"), displayDate(record.nextDoseDate)], [_t("health.lbl.note"), record.note]].filter(([, v]) => v);
  }
  if (record.type === "emergency") {
    return [[_t("health.lbl.blood"), record.bloodType], [_t("health.lbl.person"), record.contactName], [_t("health.lbl.relation"), record.relation], [_t("health.lbl.phone"), record.phone], [_t("health.lbl.allergy"), record.criticalAllergies], [_t("health.lbl.condition"), record.criticalConditions], [_t("health.lbl.medicine"), record.criticalMedicines], [_t("health.lbl.note"), record.note]].filter(([, v]) => v);
  }
  if (record.type === "note") {
    return [[_t("health.lbl.category"), record.category], [_t("health.lbl.date"), displayDate(record.date)], [_t("health.lbl.note"), record.note]].filter(([, v]) => v);
  }
  if (record.type === "report") {
    return [[_t("health.lbl.reportType"), record.reportType], [_t("health.lbl.date"), displayDate(record.date)], [_t("health.lbl.institution"), record.institution], [_t("health.lbl.doctor"), record.doctor], [_t("health.lbl.summary"), record.resultSummary], [_t("health.lbl.note"), record.note]].filter(([, v]) => v);
  }
  return [];
};

const getHealthProfileFields = (t) => {
  const _t = (k) => (typeof t === "function" ? t(k) : k);
  return [
    {
      key: "bloodType",
      label: _t("profile.bloodType"),
      icon: "🩸",
      type: "select",
      options: ["A Rh+", "A Rh-", "B Rh+", "B Rh-", "AB Rh+", "AB Rh-", "0 Rh+", "0 Rh-"],
    },
    { key: "height", label: _t("health.prof.height"), icon: "📏", placeholder: "178 cm" },
    { key: "weight", label: _t("health.prof.weight"), icon: "⚖️", placeholder: "75 kg" },
    { key: "chronicDiseases", label: _t("health.prof.chronicDiseases"), icon: "🫀", multiline: true },
    { key: "allergies", label: _t("health.card.allergy"), icon: "⚠️", multiline: true },
    { key: "regularMedicines", label: _t("health.prof.regularMedicines"), icon: "💊", multiline: true },
    { key: "surgeries", label: _t("health.prof.surgeries"), icon: "🏥", multiline: true },
    { key: "specialCondition", label: _t("health.prof.specialCondition"), icon: "♿", multiline: true },
    { key: "smokingAlcohol", label: _t("health.prof.smokingAlcohol"), icon: "🚭", placeholder: _t("health.prof.smokingAlcohol") },
    { key: "familyDoctor", label: _t("health.prof.familyDoctor"), icon: "👨‍⚕️", placeholder: _t("health.prof.familyDoctor") },
    { key: "preferredHospital", label: _t("health.prof.preferredHospital"), icon: "🏨", placeholder: _t("health.prof.preferredHospital") },
    { key: "emergencyPerson", label: _t("health.prof.emergencyPerson"), icon: "🚨", placeholder: _t("health.prof.emergencyPerson") },
    { key: "emergencyRelation", label: _t("health.prof.emergencyRelation"), icon: "👨‍👩‍👧", placeholder: _t("health.prof.emergencyRelation") },
    { key: "emergencyPhone", label: _t("health.prof.emergencyPhone"), icon: "📞", placeholder: "05xx xxx xx xx" },
    { key: "healthNote", label: _t("health.prof.notes"), icon: "📝", multiline: true },
  ];
};

const calcAge = (birthDate, t, lang = "tr") => {
  if (!birthDate) return t("health.noAge");

  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return t("health.noAge");

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }

  return lang === "en" ? `${age} years old` : `${age} yaş`;
};

const APPLE_HEALTH_EMPTY_DATA = {
  available: true,
  hasAnyData: false,
  updatedAt: null,
  metrics: {},
  history: {},
};

const appleHealthMetricDefinitions = (t) => [
  { key: "steps", label: t("health.apple.steps"), unit: "", tone: "blue" },
  { key: "activeEnergy", label: t("health.apple.energy"), unit: "kcal", tone: "orange" },
  { key: "distance", label: t("health.apple.distance"), unit: "km", tone: "green" },
];

const appleHealthDetailDefinitions = (t) => [
  ...appleHealthMetricDefinitions(t),
  { key: "exercise", label: t("health.apple.exercise"), unit: t("health.apple.minuteUnit"), tone: "violet" },
  { key: "sleep", label: t("health.apple.sleep"), unit: t("health.apple.hourUnit"), tone: "indigo" },
  { key: "restingHeartRate", label: t("health.apple.restingHeartRate"), unit: t("health.apple.bpmUnit"), tone: "rose" },
  { key: "heartRate", label: t("health.apple.latestHeartRate"), unit: t("health.apple.bpmUnit"), tone: "red" },
];

const APPLE_HEALTH_METRIC_ASSETS = {
  steps: healthStepsAsset,
  activeEnergy: healthEnergyAsset,
  distance: healthDistanceAsset,
};

const formatAppleHealthValue = (key, value, lang, withUnit = true) => {
  const numeric = Number(value);
  if (value == null || Number.isNaN(numeric)) return "—";

  const locale = lang === "en" ? "en-US" : "tr-TR";
  let displayValue = numeric;
  let maximumFractionDigits = 0;
  let unit = "";

  if (key === "distance") {
    maximumFractionDigits = 1;
    unit = "km";
  } else if (key === "activeEnergy") {
    unit = "kcal";
  } else if (key === "exercise") {
    unit = lang === "en" ? "min" : "dk";
  } else if (key === "sleep") {
    displayValue = numeric / 60;
    maximumFractionDigits = 1;
    unit = lang === "en" ? "hr" : "sa";
  } else if (key === "restingHeartRate" || key === "heartRate") {
    unit = "BPM";
  }

  const formatted = new Intl.NumberFormat(locale, {
    maximumFractionDigits,
    minimumFractionDigits: maximumFractionDigits && displayValue > 0 ? 1 : 0,
  }).format(displayValue);
  return withUnit && unit ? `${formatted} ${unit}` : formatted;
};

function AppleHealthIcon({ type }) {
  const metricAsset = APPLE_HEALTH_METRIC_ASSETS[type];
  if (metricAsset) {
    return <img className="appleHealthAssetIcon" src={metricAsset} alt="" aria-hidden="true" draggable="false" />;
  }

  if (type === "steps") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.2 3.3c1.4 0 2.3 1.2 2.1 2.6-.2 1.5-1.4 2.8-2.8 2.8-1.4 0-2.3-1.2-2.1-2.7.2-1.4 1.4-2.7 2.8-2.7Zm7.6 8c1.4 0 2.3 1.2 2.1 2.6-.2 1.5-1.4 2.8-2.8 2.8-1.4 0-2.3-1.2-2.1-2.7.2-1.4 1.4-2.7 2.8-2.7ZM6.9 9.7c2.2 0 4.1 1.7 4.4 4l.2 1.6c.2 1.6-1 3-2.6 3H5.5a3.4 3.4 0 0 1-3.3-4.1l.3-1.5a4.5 4.5 0 0 1 4.4-3Zm8.2 7.9c2.2 0 4.1 1.7 4.4 4l.1.4H12l.1-.4a3.1 3.1 0 0 1 3-4Z" /></svg>
    );
  }
  if (type === "activeEnergy") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13.4 2.3c.5 3.2-1.7 4.7-3.3 6.7-1.2 1.5-1.7 3.1-.8 4.8.3-1.8 1.5-2.8 2.8-3.9.1 2.1 2.4 2.9 2.5 5.3 0 1.5-.8 2.6-1.9 3.4 2.7-.4 4.7-2.5 4.7-5.7 0-3.6-2.6-6.9-4-10.6ZM8.1 7.8C5.8 9.7 4.7 12 4.7 14.5A7.3 7.3 0 0 0 12 21.8c.7 0 1.5-.1 2.2-.3-3.4-.2-6.2-2.9-6.2-6.4 0-1.6.6-3.1 1.6-4.4-.9-.8-1.4-1.7-1.5-2.9Z" /></svg>
    );
  }
  if (type === "distance") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5a5.4 5.4 0 0 0-5.4 5.4c0 3.9 5.4 9.9 5.4 9.9s5.4-6 5.4-9.9A5.4 5.4 0 0 0 12 2.5Zm0 7.4a2.1 2.1 0 1 1 0-4.2 2.1 2.1 0 0 1 0 4.2ZM5.5 17.2C3.3 17.7 2 18.5 2 19.5c0 1.5 4.5 2.7 10 2.7s10-1.2 10-2.7c0-1-1.3-1.8-3.5-2.3l-1.1 1.5c1.2.2 2 .5 2 .8 0 .6-3.3 1.1-7.4 1.1s-7.4-.5-7.4-1.1c0-.3.8-.6 2-.8l-1.1-1.5Z" /></svg>
    );
  }
  if (type === "sleep") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.6 15.2A8.7 8.7 0 0 1 9 3.5a9.1 9.1 0 1 0 11.6 11.7Z" /></svg>;
  }
  if (type === "exercise") {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7.2 5.2 4.8 7.6l2 2L9.2 7l-2-1.8Zm9.6 0-2 1.8 2.4 2.6 2-2-2.4-2.4ZM9.4 8.4l5.2.1 1 4.6-2 1.7-.2 6h-2.7l-.2-5.1-1.8-1.8-3.2 2.5-1.7-2.1 4.3-3.7 1.3-2.2Zm2.6-6a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Z" /></svg>;
  }
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-8-4.8-8-11a4.5 4.5 0 0 1 8-2.8A4.5 4.5 0 0 1 20 10c0 6.2-8 11-8 11Zm-5.2-9.8h3l1-2.3 2.2 5.4 1.1-3.1h3.1" /></svg>;
}

function AppleHealthActivityStrip({ status, data, loading, onOpen }) {
  const { t, lang } = useLanguage();
  const metrics = appleHealthMetricDefinitions(t);
  const needsConnection = status?.shouldRequest !== false;

  return (
    <div className="appleHealthStrip" aria-label={t("health.apple.todaySummary")}>
      <div className="appleHealthStripHead">
        <span className="appleHealthSourceMark"><i />{t("health.apple.sourceToday")}</span>
        <span className={`appleHealthStripStatus ${needsConnection ? "connect" : ""}`}>
          {needsConnection ? t("health.apple.connect") : t("health.apple.live")}
        </span>
      </div>
      <div className="appleHealthMetricGrid">
        {metrics.map((metric) => {
          const value = data?.metrics?.[metric.key];
          const spokenValue = formatAppleHealthValue(metric.key, value, lang, true);
          return (
            <button
              type="button"
              key={metric.key}
              className={`appleHealthMetric ${metric.tone} ${loading ? "loading" : ""}`}
              onClick={() => onOpen(metric.key)}
              aria-label={`${metric.label}: ${spokenValue}. ${t("health.apple.openSummary")}`}
            >
              <span className="appleHealthMetricIcon"><AppleHealthIcon type={metric.key} /></span>
              <span className="appleHealthMetricValue">{loading ? "" : formatAppleHealthValue(metric.key, value, lang, false)}</span>
              <span className="appleHealthMetricLabel">
                {metric.label}{metric.unit && value != null ? <small>{metric.unit}</small> : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AppleHealthTrendCard({ metric, values = [] }) {
  const { t, lang } = useLanguage();
  const numericValues = values.map((item) => Number(item?.value)).filter(Number.isFinite);
  const max = Math.max(1, ...numericValues);
  const locale = lang === "en" ? "en-US" : "tr-TR";

  return (
    <section className={`appleHealthTrendCard ${metric.tone}`}>
      <div className="appleHealthTrendHead">
        <div><b>{metric.label}</b><span>{t("health.apple.last7Days")}</span></div>
        <span className="appleHealthTrendIcon"><AppleHealthIcon type={metric.key} /></span>
      </div>
      <div className="appleHealthBars" aria-label={`${metric.label} ${t("health.apple.last7Days")}`}>
        {values.map((item) => {
          const value = Number(item?.value);
          const hasValue = Number.isFinite(value);
          const date = item?.date ? new Date(`${item.date}T12:00:00`) : null;
          const day = date && !Number.isNaN(date.getTime())
            ? new Intl.DateTimeFormat(locale, { weekday: "narrow" }).format(date)
            : "";
          return (
            <div className="appleHealthBarItem" key={item?.date || Math.random()}>
              <span className="appleHealthBarValue">{hasValue ? formatAppleHealthValue(metric.key, value, lang, false) : ""}</span>
              <span className="appleHealthBarTrack"><i style={{ height: hasValue ? `${Math.max(8, (value / max) * 100)}%` : "4%" }} /></span>
              <span className="appleHealthBarDay">{day}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function AppleHealthSummaryPage({ status, data, loading, error, selectedMetric, onBack, onAuthorize, onRefresh, onSettings }) {
  const { t, lang } = useLanguage();
  const [authorizing, setAuthorizing] = useState(false);
  const mainMetrics = appleHealthMetricDefinitions(t);
  const detailMetrics = appleHealthDetailDefinitions(t).slice(3);
  const needsPermission = status?.available !== false && status?.shouldRequest !== false;
  const hasAnyData = data?.hasAnyData === true;
  const updatedAt = data?.updatedAt ? new Date(data.updatedAt) : null;
  const locale = lang === "en" ? "en-US" : "tr-TR";

  const authorize = async () => {
    setAuthorizing(true);
    try { await onAuthorize(); } catch (_) {} finally { setAuthorizing(false); }
  };

  return (
    <div className="pageContent healthDetailPageWrapper appleHealthPage">
      <div className="belgeAltSayfaHeader appleHealthHeader">
        <button className="belgeGeriBtn" type="button" onClick={onBack} aria-label={t("common.back")}>‹</button>
        <div className="appleHealthHeaderGlyph"><AppleHealthIcon type="heartRate" /></div>
        <div>
          <div className="belgeAltSayfaBaslik">{t("health.apple.title")}</div>
          <div className="belgeAltSayfaAlt">{t("health.apple.privateOnDevice")}</div>
        </div>
      </div>

      {status?.available === false ? (
        <section className="appleHealthPermissionCard unavailable">
          <div className="appleHealthPermissionIcon"><AppleHealthIcon type="heartRate" /></div>
          <h2>{t("health.apple.unavailableTitle")}</h2>
          <p>{t("health.apple.unavailableDesc")}</p>
        </section>
      ) : needsPermission ? (
        <section className="appleHealthPermissionCard">
          <div className="appleHealthPermissionAura"><AppleHealthIcon type="heartRate" /></div>
          <span className="appleHealthEyebrow">{t("health.apple.permissionEyebrow")}</span>
          <h2>{t("health.apple.permissionTitle")}</h2>
          <p>{t("health.apple.permissionDesc")}</p>
          <div className="appleHealthPermissionTypes">
            {mainMetrics.map((item) => <span key={item.key}><AppleHealthIcon type={item.key} />{item.label}</span>)}
            <span><AppleHealthIcon type="exercise" />{t("health.apple.exercise")}</span>
            <span><AppleHealthIcon type="sleep" />{t("health.apple.sleep")}</span>
            <span><AppleHealthIcon type="heartRate" />{t("health.apple.heartData")}</span>
          </div>
          <div className="appleHealthPermissionActions">
            <button type="button" className="appleHealthPrimaryBtn" onClick={authorize} disabled={authorizing}>
              {authorizing ? t("health.apple.connecting") : t("health.apple.continue")}
            </button>
            <button type="button" className="appleHealthSecondaryBtn" onClick={onBack}>{t("health.apple.notNow")}</button>
          </div>
          <small className="appleHealthPrivacyLine">{t("health.apple.permissionPrivacy")}</small>
          {error && <div className="appleHealthInlineError">{t("health.apple.loadFailed")}</div>}
        </section>
      ) : (
        <>
          <section className="appleHealthHero">
            <div className="appleHealthHeroTop">
              <div>
                <span>{t("health.apple.today")}</span>
                <h2>{t("health.apple.dailyMovement")}</h2>
              </div>
              <button type="button" className="appleHealthRefreshBtn" onClick={onRefresh} disabled={loading} aria-label={t("health.apple.refresh")}>
                <span className={loading ? "spinning" : ""}>↻</span>
              </button>
            </div>
            <div className="appleHealthHeroMetrics">
              {mainMetrics.map((metric) => (
                <div key={metric.key} className={`${metric.tone} ${selectedMetric === metric.key ? "selected" : ""}`}>
                  <span><AppleHealthIcon type={metric.key} /></span>
                  <b>{formatAppleHealthValue(metric.key, data?.metrics?.[metric.key], lang, false)}</b>
                  <small>{metric.label}{metric.unit && data?.metrics?.[metric.key] != null ? ` · ${metric.unit}` : ""}</small>
                </div>
              ))}
            </div>
          </section>

          {!hasAnyData && !loading && (
            <section className="appleHealthNoData">
              <span><AppleHealthIcon type="heartRate" /></span>
              <div><b>{t("health.apple.noDataTitle")}</b><p>{t("health.apple.noDataDesc")}</p></div>
            </section>
          )}
          {error && <div className="appleHealthInlineError">{t("health.apple.loadFailed")}</div>}

          <div className="appleHealthDetailGrid">
            {detailMetrics.map((metric) => (
              <section className={`appleHealthDetailMetric ${metric.tone}`} key={metric.key}>
                <span className="appleHealthDetailIcon"><AppleHealthIcon type={metric.key} /></span>
                <div><small>{metric.label}</small><b>{formatAppleHealthValue(metric.key, data?.metrics?.[metric.key], lang, true)}</b></div>
              </section>
            ))}
          </div>

          <div className="appleHealthSectionTitle"><div><b>{t("health.apple.weeklyTrends")}</b><span>{t("health.apple.weeklyTrendsDesc")}</span></div></div>
          <div className="appleHealthTrends">
            {mainMetrics.map((metric) => <AppleHealthTrendCard key={metric.key} metric={metric} values={data?.history?.[metric.key] || []} />)}
          </div>

          <section className="appleHealthTrustCard">
            <div className="appleHealthTrustRow"><span>✓</span><div><b>{t("health.apple.onDeviceTitle")}</b><p>{t("health.apple.onDeviceDesc")}</p></div></div>
            <div className="appleHealthTrustRow"><span>↗</span><div><b>{t("health.apple.permissionsTitle")}</b><p>{t("health.apple.permissionsDesc")}</p></div></div>
            <button type="button" onClick={onSettings}>{t("health.apple.openSettings")}</button>
          </section>

          <div className="appleHealthFooterNote">
            <span>{updatedAt && !Number.isNaN(updatedAt.getTime()) ? `${t("health.apple.updated")} ${new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(updatedAt)}` : t("health.apple.updatedUnknown")}</span>
            <p>{t("health.apple.disclaimer")}</p>
          </div>
        </>
      )}
    </div>
  );
}

function MyHealthProfile({ uye, user, familyId, onOwnHealthProfileUpdated, appleHealthStatus, appleHealthData, appleHealthLoading, onAppleHealthOpen, showAppleHealth }) {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [saving, setSaving] = useState(false);

  if (!uye) return null;

  const healthProfile = uye.healthProfile || {};
  const fullName = `${uye.ad || ""} ${uye.soyad || ""}`.trim() || t("health.userFallback");
  const initials = `${uye.ad?.[0] || ""}${uye.soyad?.[0] || ""}` || "FP";
  const color = uye.calendarColor || "#ef476f";

  const startEdit = (field) => {
    setEditingKey(field.key);
    setDraftValue(healthProfile[field.key] || "");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraftValue("");
  };

  const saveField = async (field) => {
    if (!user?.uid || !familyId || uye.uid !== user.uid) return;

    const updatedHealthProfile = {
      ...(healthProfile || {}),
      [field.key]: draftValue,
    };

    try {
      setSaving(true);
      const updatedUser = await kendiSaglikProfiliniGuncelleService({
        familyId,
        healthProfile: updatedHealthProfile,
        memberName: fullName,
        fieldKey: field.key,
        date: bugunKey(),
      });
      cancelEdit();
      if (onOwnHealthProfileUpdated) {
        onOwnHealthProfileUpdated({
          healthProfile: updatedUser?.healthProfile || updatedHealthProfile,
          user: updatedUser,
        });
      }
    } catch (err) {
      appAlert(err.message || t("health.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="myHealthProfileCard">
      <div className="childrenProfileGlow" />

      <AvatarImg
        avatar={uye.avatar || null}
        renk={color}
        initials={initials}
        size={80}
        glow={true}
        radius="26px"
        className="childrenAvatarBig myHealthAvatar"
      
                      uid={uye.uid || uye.id}
                      isOnline={uye.isOnline === true || uye.online === true}
                    />

      <div className="childrenProfileMain myHealthProfileMain">
        <div className="childrenProfileLabel">{t("health.myProfile")}</div>
        <h2>{fullName}</h2>
        <p>
          {calcAge(uye.dogumTarihi, t, lang)} · {hl(lang, uye.cinsiyet || "Cinsiyet belirtilmedi")}
        </p>

        {showAppleHealth ? (
          <AppleHealthActivityStrip
            status={appleHealthStatus}
            data={appleHealthData}
            loading={appleHealthLoading}
            onOpen={onAppleHealthOpen}
          />
        ) : (
          <div className="childrenProfileBadges">
            <span>🎂 {uye.dogumTarihi || hl(lang, "Belirtilmedi")}</span>
            <span>🩸 {healthProfile.bloodType || hl(lang, "Belirtilmedi")}</span>
            <span>👤 {hl(lang, uye.rol || "Belirtilmedi")}</span>
          </div>
        )}
      </div>

      <div
        className="childrenProfileToggleHead myHealthToggleHead"
        onClick={() => setOpen((prev) => !prev)}
      >
        <div>
          <b>{t("health.myInfo")}</b>
          <span>{t("health.myInfoDesc")}</span>
        </div>

        <button
  type="button"
  className="childrenProfileToggleBtn"
  onClick={(e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  }}
>
          {open ? "−" : "+"}
        </button>
      </div>

      {open && (
        <div className="myHealthProfileInfo">
          {getHealthProfileFields(t).map((field) => {
            const value = healthProfile[field.key] || "";
            const isEditing = editingKey === field.key;

            return (
              <div className="myHealthInfoItem" key={field.key}>
                <div className="myHealthInfoTop">
                  <b>
                    {field.icon} {field.label}
                  </b>

                  {!isEditing && (
                    <button type="button" onClick={() => startEdit(field)} title={t("common.edit")}>
                      ✎
                    </button>
                  )}
                </div>

                {isEditing ? (
                  <div className="myHealthEditArea">
                    {field.type === "select" ? (
                      <select
                        className="darkInput"
                        value={draftValue}
                        onChange={(e) => setDraftValue(limitHealthText(field.key, e.target.value))}
                      >
                        <option value="">{t("health.select")}</option>
                        {field.options.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : field.multiline ? (
                      <textarea
                        className="darkInput"
                        rows={3}
                        placeholder={field.placeholder || `${field.label} ${hl(lang, "gir")}`}
                        value={draftValue}
                        maxLength={healthTextLimitForField(field.key)}
                        onChange={(e) => setDraftValue(limitHealthText(field.key, e.target.value))}
                      />
                    ) : (
                      <input
                        className="darkInput"
                        placeholder={field.placeholder || `${field.label} ${hl(lang, "gir")}`}
                        value={draftValue}
                        maxLength={healthTextLimitForField(field.key)}
                        onChange={(e) => setDraftValue(limitHealthText(field.key, e.target.value))}
                      />
                    )}

                    <div className="myHealthEditActions">
                      <button type="button" onClick={() => saveField(field)} disabled={saving}>
                        {saving ? hl(lang, "Kaydediliyor...") : hl(lang, "Kaydet")}
                      </button>
                      <button type="button" onClick={cancelEdit} disabled={saving}>
                        {hl(lang, "İptal")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <ExpandableHealthText>{hl(lang, value || "Belirtilmedi")}</ExpandableHealthText>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function getRecordTitle(r, t) {
  const _t = t || ((k) => k);
  return r.name || r.title || r.doctor || r.value || _t("health.record.recordCount");
}

function getRecordSub(r, t, lang = "tr") {
  const _t = t || ((k) => k);
  if (r.type === "medicine") {
    const stats = getMedicineTodayStats(r);
    const times = normalizeMedicineTimes(r.times || []).join(" · ");
    return `${r.dose || ""} · ${stats.taken}/${stats.total || 0}${times ? ` · ${times}` : ""}`.trim();
  }
  if (r.type === "appointment") return `${hl(lang, r.branch || _t("health.appointment.default"))} · ${r.date || ""} ${r.time || ""}`.trim();
  if (r.type === "measurement") return `${hl(lang, r.measurementType || _t("health.measurement.default"))} · ${r.value || ""}`.trim();
  if (r.type === "allergy") return `${hl(lang, r.level || _t("health.notSpecified"))}`;
  if (r.type === "condition") return r.active === false ? _t("health.status.passive") : _t("health.status.active");
  if (r.type === "vaccine") return r.done ? _t("health.status.dotDone") : _t("health.status.dotPending");
  if (r.type === "emergency") return r.bloodType ? `${r.bloodType}` : _t("health.summary.emergencyCard");
  if (r.type === "report") return hl(lang, r.reportType || r.note || _t("health.card.report"));
  return r.note || r.description || "";
}

function ExpandableHealthText({ children, className = "" }) {
  const [expanded, setExpanded] = useState(false);
  const text = String(children ?? "");
  const toggle = (event) => {
    event.stopPropagation();
    setExpanded((value) => !value);
  };

  return (
    <span
      className={`healthExpandableText ${expanded ? "expanded" : ""} ${className}`.trim()}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggle(event);
        }
      }}
    >
      {text}
    </span>
  );
}

/* ── DatalistInput — ref tabanlı, dropdown garantili ── */
function DatalistInput({ id, placeholder, value, onChange, options, lang = "tr", validationClass = "", requiredField, invalid = false }) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState(value || "");
  const wrapRef = useRef(null);

  // Dışarı tıklayınca kapat
  useEffect(() => {
    if (!open) return;
    const fn = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    document.addEventListener("touchstart", fn);
    return () => { document.removeEventListener("mousedown", fn); document.removeEventListener("touchstart", fn); };
  }, [open]);

  // value dışarıdan değişirse senkronize et
  useEffect(() => { setFilter(value || ""); }, [value]);

  const filtered = filter
    ? options.filter(o => o.toLowerCase().includes(filter.toLowerCase()) || hl(lang, o).toLowerCase().includes(filter.toLowerCase()))
    : options;

  const select = (opt) => {
    onChange(opt);
    setFilter(opt);
    setOpen(false);
  };

  return (
    <div className={`datalistInputWrap ${validationClass}`.trim()} ref={wrapRef} style={{ position: "relative" }} data-required-field={requiredField} aria-invalid={invalid}>
      <input
        ref={inputRef}
        className="darkInput"
        placeholder={placeholder}
        value={lang === "en" ? hl(lang, filter) : filter}
        autoComplete="off"
        maxLength={HEALTH_TEXT_LIMITS.short}
        onChange={(e) => {
          const nextValue = limitHealthText("title", e.target.value);
          setFilter(nextValue);
          onChange(healthCanonical(nextValue));
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        style={{ paddingRight: "44px" }}
      />
      <button
        type="button"
        className="datalistOpenBtn"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => { setOpen(v => !v); inputRef.current?.focus(); }}
      >▾</button>
      {open && filtered.length > 0 && (
        <div className="datalistDropdown">
          {filtered.map(opt => (
            <button
              key={opt}
              type="button"
              className="datalistDropdownItem"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(opt)}
            >
              {hl(lang, opt)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HealthAddForm({ type, color, onCancel, onSave, initialData = null }) {
  const { t, lang } = useLanguage();
  const healthValidation = useFormValidation(`health-${type}`);
  const inputRef = useRef(null);
  const submitLockRef = useRef(false);
  const [dosya, setDosya] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState(() => {
    if (!initialData) {
      if (type === "measurement") return { date: bugunKey(), measurementType: "" };
      if (type === "appointment") return { date: bugunKey(), time: simdikiSaatKey(), addToCalendar: true, createTask: false };
      if (type === "vaccine") return { plannedDate: bugunKey(), done: false };
      if (type === "note") return { date: bugunKey(), category: "Genel" };
      if (type === "condition") return { active: true };
      if (type === "report") return { date: bugunKey() };
      return {};
    }

    if (initialData.type === "medicine") {
      return {
        name: initialData.name || "",
        dose: initialData.dose || "",
        timesText: initialData.times?.join(",") || "",
        startDate: initialData.startDate || "",
        endDate: initialData.endDate || "",
        note: initialData.note || "",
      };
    }

    return { ...initialData };
  });

  const set = (key, value) => {
    setForm((p) => ({ ...p, [key]: limitHealthText(key, value) }));
    healthValidation.clearError(key);
  };

  const setMeasurementType = (value) => {
    const measurementType = healthCanonical(value);
    setForm((previous) => ({
      ...previous,
      measurementType,
      systolic: "",
      diastolic: "",
      value: "",
      unit: bodyMeasurementUnit(measurementType),
    }));
    healthValidation.clearError("measurementType");
    healthValidation.clearError("value");
  };

  const requiredErrors = () => {
    if (type === "medicine") return [!form.name?.trim() && "name", !form.dose?.trim() && "dose", normalizeMedicineTimes(form.timesText || "").length === 0 && "timesText", !form.startDate && "startDate"];
    if (type === "appointment") return [!form.doctor?.trim() && "doctor", !form.date && "date", !form.time && "time"];
    if (type === "measurement") return [!form.measurementType && "measurementType", form.measurementType === "Tansiyon" && !form.systolic && "systolic", form.measurementType === "Tansiyon" && !form.diastolic && "diastolic", form.measurementType !== "Tansiyon" && !form.value?.trim() && "value"];
    if (type === "allergy") return [!form.name?.trim() && "name"];
    if (type === "condition") return [!form.title?.trim() && "title"];
    if (type === "vaccine") return [!form.title?.trim() && "title", !form.plannedDate && "plannedDate"];
    if (type === "emergency") return [!form.contactName?.trim() && "contactName", !form.phone?.trim() && "phone"];
    if (type === "note") return [!form.title?.trim() && "title", !form.note?.trim() && "note"];
    if (type === "report") return [!form.title?.trim() && "title"];
    return [];
  };

  const requiredLabels = {
    name: type === "medicine" ? hl(lang, "İlaç adı") : hl(lang, "Kayıt adı"),
    dose: hl(lang, "Doz"),
    timesText: hl(lang, "Saatler"),
    startDate: hl(lang, "Başlangıç tarihi"),
    doctor: hl(lang, "Doktor / Hastane"),
    date: hl(lang, "Tarih"),
    time: hl(lang, "Saat"),
    measurementType: t("health.measurement.typePlaceholder"),
    systolic: hl(lang, "Büyük tansiyon"),
    diastolic: hl(lang, "Küçük tansiyon"),
    value: hl(lang, "Değer"),
    title: type === "report" ? hl(lang, "Tahlil / rapor adı") : hl(lang, "Başlık"),
    plannedDate: hl(lang, "Planlanan aşı tarihi"),
    contactName: hl(lang, "Acil kişi"),
    phone: hl(lang, "Telefon"),
    note: hl(lang, "Sağlık notu"),
  };

  const save = async () => {
    if (submitLockRef.current) return;
    if (!healthValidation.validate(requiredErrors())) return;
    submitLockRef.current = true;
    setSubmitting(true);
    try {
      await onSave({ ...form, dosya });
    } catch (error) {
      appAlert(error?.message || t("health.saveFailed"));
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  const sectionIntro = {
    appointment: ["📅", t("health.card.appointment"), t("health.section.intro.appointment")],
    measurement: ["🩺", t("health.card.measurement"), t("health.section.intro.measurement")],
    report: ["📄", t("health.card.report"), t("health.section.intro.report") || ""],
    allergy: ["⚠️", t("health.card.allergy"), t("health.section.intro.allergy") || ""],
    condition: ["🫀", t("health.card.condition"), t("health.section.intro.condition") || ""],
    vaccine: ["💉", t("health.card.vaccine"), t("health.section.intro.vaccine") || ""],
    emergency: ["🚨", t("health.card.emergency"), t("health.section.intro.emergency") || ""],
    note: ["📝", t("health.card.note"), t("health.section.intro.note") || ""],
  }[type];

  return (
    <div className="healthAddForm">
      {type !== "medicine" && sectionIntro && (
        <div className="healthFormHero" style={{ borderColor: `${color}33`, background: `${color}12` }}>
          <span>{sectionIntro[0]}</span>
          <div>
            <b>{sectionIntro[1]}</b>
            <small>{sectionIntro[2]}</small>
          </div>
        </div>
      )}

      {type === "medicine" && (
        <>
          <div className="medicineFormHero">
            <span>💊</span>
            <div>
              <b>{t("health.medicine.planTitle")}</b>
              <small>{t("health.medicine.planDesc")}</small>
            </div>
          </div>

          <input className={healthValidation.fieldClass("name", "darkInput")} data-required-field={`health-${type}-name`} aria-invalid={healthValidation.hasError("name")} maxLength={healthTextLimitForField("name")} placeholder={hl(lang, "İlaç adı · Örn: Parol")} value={form.name || ""} onChange={(e) => set("name", e.target.value)} />
          <div className="formRow">
            <input className={healthValidation.fieldClass("dose", "darkInput")} data-required-field={`health-${type}-dose`} aria-invalid={healthValidation.hasError("dose")} placeholder={hl(lang, "Doz · Örn: 500 mg")} value={form.dose || ""} onChange={(e) => set("dose", e.target.value)} />
            <input className={healthValidation.fieldClass("timesText", "darkInput")} data-required-field={`health-${type}-timesText`} aria-invalid={healthValidation.hasError("timesText")} placeholder={hl(lang, "Saatler · 08:00, 14:00, 20:00")} value={form.timesText || ""} onChange={(e) => set("timesText", e.target.value)} />
          </div>
          <div className="medicineQuickTimes">
            {["08:00, 20:00", "09:00, 15:00, 21:00", "10:00", "22:00"].map((preset) => (
              <button key={preset} type="button" onClick={() => set("timesText", preset)}>
                {preset}
              </button>
            ))}
          </div>
          <div className="formRow">
            <div className={healthValidation.fieldClass("startDate", `healthDateInline ${form.startDate ? "hasValue" : ""}`)} data-required-field={`health-${type}-startDate`}><span>{hl(lang, "Başlangıç tarihi")}</span><input className="darkInput" aria-invalid={healthValidation.hasError("startDate")} type="date" value={form.startDate || ""} onChange={(e) => set("startDate", e.target.value)} /></div>
            <div className={`healthDateInline ${form.endDate ? "hasValue" : ""}`}><span>{hl(lang, "Bitiş tarihi")}</span><input className="darkInput" type="date" value={form.endDate || ""} onChange={(e) => set("endDate", e.target.value)} /></div>
          </div>
          <textarea className="darkInput" rows={2} placeholder={hl(lang, "Not · Tok karna, yemek sonrası, doktor notu...")} value={form.note || ""} onChange={(e) => set("note", e.target.value)} />
        </>
      )}

      {type === "appointment" && (
        <>
          <input className={healthValidation.fieldClass("doctor", "darkInput")} data-required-field={`health-${type}-doctor`} aria-invalid={healthValidation.hasError("doctor")} placeholder={hl(lang, "Doktor / Hastane")} value={form.doctor || ""} onChange={(e) => set("doctor", e.target.value)} />
          <div className="formRow">
            <DatalistInput id="branchOptions" placeholder={hl(lang, "Branş seç veya yaz")} value={form.branch || ""} onChange={(v) => set("branch", v)} options={BRANCH_OPTIONS} lang={lang} />
            <DatalistInput id="appointmentTypeOptions" placeholder={t("health.appointment.type")} value={form.appointmentType || ""} onChange={(v) => set("appointmentType", v)} options={APPOINTMENT_TYPES} lang={lang} />
          </div>
          <div className="formRow">
            <div className={healthValidation.fieldClass("date", `healthDateInline ${form.date ? "hasValue" : ""}`)} data-required-field={`health-${type}-date`}><span>{hl(lang, "Randevu tarihi")}</span><input className="darkInput" aria-invalid={healthValidation.hasError("date")} type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
            <div className={healthValidation.fieldClass("time", `healthDateInline healthTimeInline ${form.time ? "hasValue" : ""}`)} data-required-field={`health-${type}-time`}><span>{hl(lang, "Randevu saati")}</span><input className="darkInput" aria-invalid={healthValidation.hasError("time")} type="time" value={form.time || ""} onChange={(e) => set("time", e.target.value)} /></div>
          </div>
          <input className="darkInput" placeholder={hl(lang, "Konum / hastane adresi")} value={form.location || ""} onChange={(e) => set("location", e.target.value)} />
          <textarea className="darkInput" rows={2} placeholder={t("health.appointment.note")} value={form.note || ""} onChange={(e) => set("note", e.target.value)} />
          <div className="healthToggleMiniGrid">
            <label><input type="checkbox" checked={form.addToCalendar !== false} onChange={(e) => set("addToCalendar", e.target.checked)} /> {t("health.addToCalendar")}</label>
            <label><input type="checkbox" checked={!!form.createTask} onChange={(e) => set("createTask", e.target.checked)} /> {t("health.createTask")}</label>
          </div>
        </>
      )}

      {type === "measurement" && (
        <>
          <DatalistInput id="measurementOptions" placeholder={t("health.measurement.typePlaceholder")} value={form.measurementType || ""} onChange={setMeasurementType} options={MEASUREMENT_OPTIONS} lang={lang} validationClass={healthValidation.fieldClass("measurementType")} requiredField={`health-${type}-measurementType`} invalid={healthValidation.hasError("measurementType")} />
          {form.measurementType === "Tansiyon" ? (
            <div className="formRow">
              <input className={healthValidation.fieldClass("systolic", "darkInput")} data-required-field={`health-${type}-systolic`} aria-invalid={healthValidation.hasError("systolic")} inputMode="numeric" placeholder={hl(lang, "Büyük · 120")} value={form.systolic || ""} onChange={(e) => set("systolic", e.target.value)} />
              <input className={healthValidation.fieldClass("diastolic", "darkInput")} data-required-field={`health-${type}-diastolic`} aria-invalid={healthValidation.hasError("diastolic")} inputMode="numeric" placeholder={hl(lang, "Küçük · 80")} value={form.diastolic || ""} onChange={(e) => set("diastolic", e.target.value)} />
            </div>
          ) : (
            <div className="formRow">
              <input className={healthValidation.fieldClass("value", "darkInput")} data-required-field={`health-${type}-value`} aria-invalid={healthValidation.hasError("value")} inputMode={bodyMeasurementUnit(form.measurementType) ? "decimal" : undefined} placeholder={hl(lang, "Değer")} value={form.value || ""} onChange={(e) => set("value", e.target.value)} />
              <input className="darkInput" placeholder={hl(lang, "Birim · mg/dL, kg, °C")} value={bodyMeasurementUnit(form.measurementType) || form.unit || ""} readOnly={!!bodyMeasurementUnit(form.measurementType)} onChange={(e) => set("unit", e.target.value)} />
            </div>
          )}
          <div className="formRow">
            <input className="darkInput" type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} />
            <select className="darkInput" value={form.context || ""} onChange={(e) => set("context", e.target.value)}>
              <option value="">{hl(lang, "Durum seç")}</option>
              <option>{t("health.bloodContext.ac")}</option><option>{t("health.bloodContext.tok")}</option><option>{t("health.bloodContext.rest")}</option><option>{t("health.bloodContext.postEx")}</option><option>{t("health.bloodContext.night")}</option>
            </select>
          </div>
          <input className="darkInput" placeholder={hl(lang, "Not")} value={form.note || ""} onChange={(e) => set("note", e.target.value)} />
        </>
      )}

      {type === "allergy" && (
        <>
          <DatalistInput id="allergyOptions" placeholder={t("health.placeholder.allergy")} value={form.name || ""} onChange={(v) => set("name", v)} options={ALLERGY_OPTIONS} lang={lang} validationClass={healthValidation.fieldClass("name")} requiredField={`health-${type}-name`} invalid={healthValidation.hasError("name")} />
          <div className="formRow">
            <select className="darkInput" value={form.allergyType || ""} onChange={(e) => set("allergyType", e.target.value)}>
              <option value="">{hl(lang, "Tür seç")}</option>{ALLERGY_TYPES.map((x)=><option key={x} value={x}>{hl(lang, x)}</option>)}
            </select>
            <select className="darkInput" value={form.level || ""} onChange={(e) => set("level", e.target.value)}>
              <option value="">{hl(lang, "Şiddet seç")}</option>{["Hafif","Orta","Ciddi","Acil"].map((x)=><option key={x} value={x}>{hl(lang, x)}</option>)}
            </select>
          </div>
          <input className="darkInput" placeholder={hl(lang, "Belirti · kaşıntı, nefes darlığı...")} value={form.symptoms || ""} onChange={(e) => set("symptoms", e.target.value)} />
          <input className="darkInput" placeholder={hl(lang, "Acil durumda yapılacak")} value={form.action || ""} onChange={(e) => set("action", e.target.value)} />
          <textarea className="darkInput" rows={2} placeholder={hl(lang, "Kaçınılacak şeyler / not")} value={form.avoid || form.note || ""} onChange={(e) => set("avoid", e.target.value)} />
        </>
      )}

      {type === "condition" && (
        <>
          <DatalistInput id="conditionOptions" placeholder={t("health.placeholder.condition")} value={form.title || ""} onChange={(v) => set("title", v)} options={CONDITION_OPTIONS} lang={lang} validationClass={healthValidation.fieldClass("title")} requiredField={`health-${type}-title`} invalid={healthValidation.hasError("title")} />
          <div className="formRow">
            <input className="darkInput" type="date" value={form.diagnosisDate || ""} onChange={(e) => set("diagnosisDate", e.target.value)} />
            <select className="darkInput" value={form.active === false ? "false" : "true"} onChange={(e) => set("active", e.target.value === "true")}> <option value="true">{t("health.status.active")}</option><option value="false">{t("health.status.passive")}</option></select>
          </div>
          <input className="darkInput" placeholder={hl(lang, "Doktor / klinik")} value={form.doctor || ""} onChange={(e) => set("doctor", e.target.value)} />
          <input className="darkInput" placeholder={hl(lang, "Takip sıklığı · 6 ayda bir")} value={form.followUp || ""} onChange={(e) => set("followUp", e.target.value)} />
          <textarea className="darkInput" rows={2} placeholder={hl(lang, "Kullanılan ilaçlar / not")} value={form.medicines || form.note || ""} onChange={(e) => set("medicines", e.target.value)} />
        </>
      )}

      {type === "vaccine" && (
        <>
          <DatalistInput id="vaccineOptions" placeholder={t("health.placeholder.vaccine")} value={form.title || ""} onChange={(v) => set("title", v)} options={VACCINE_OPTIONS} lang={lang} validationClass={healthValidation.fieldClass("title")} requiredField={`health-${type}-title`} invalid={healthValidation.hasError("title")} />
          <div className="formRow vaccineDateRow">
            <input className="darkInput" placeholder={hl(lang, "Doz no · 1. doz")} value={form.doseNo || ""} onChange={(e) => set("doseNo", e.target.value)} />
            <div className={healthValidation.fieldClass("plannedDate", `healthDateInline ${form.plannedDate ? "hasValue" : ""}`)} data-required-field={`health-${type}-plannedDate`}>
              <span>{hl(lang, "Planlanan aşı tarihi")}</span>
              <input className="darkInput" aria-invalid={healthValidation.hasError("plannedDate")} type="date" value={form.plannedDate || ""} onChange={(e) => set("plannedDate", e.target.value)} />
            </div>
          </div>
          <div className="formRow vaccineDateRow">
            <select className="darkInput" value={form.done ? "true" : "false"} onChange={(e) => set("done", e.target.value === "true")}><option value="false">{t("health.status.pending")}</option><option value="true">{t("health.status.done")}</option></select>
            <div className={`healthDateInline ${form.doneDate ? "hasValue" : ""}`}>
              <span>{hl(lang, "Yapıldığı tarih")}</span>
              <input className="darkInput" type="date" value={form.doneDate || ""} onChange={(e) => set("doneDate", e.target.value)} />
            </div>
          </div>
          <div className={`healthDateInline healthDateInlineFull ${form.nextDoseDate ? "hasValue" : ""}`}>
            <span>{hl(lang, "Sonraki doz tarihi")}</span>
            <input className="darkInput" type="date" value={form.nextDoseDate || ""} onChange={(e) => set("nextDoseDate", e.target.value)} />
          </div>
          <textarea className="darkInput" rows={2} placeholder={hl(lang, "Not")} value={form.note || ""} onChange={(e) => set("note", e.target.value)} />
        </>
      )}

      {type === "emergency" && (
        <>
          <div className="formRow">
            <select className="darkInput" value={form.bloodType || ""} onChange={(e) => set("bloodType", e.target.value)}>
              <option value="">{hl(lang, "Kan grubu")}</option>{["A Rh+","A Rh-","B Rh+","B Rh-","AB Rh+","AB Rh-","0 Rh+","0 Rh-"].map((x)=><option key={x}>{x}</option>)}
            </select>
            <input className={healthValidation.fieldClass("contactName", "darkInput")} data-required-field={`health-${type}-contactName`} aria-invalid={healthValidation.hasError("contactName")} placeholder={hl(lang, "Acil kişi")} value={form.contactName || form.title || ""} onChange={(e) => set("contactName", e.target.value)} />
          </div>
          <div className="formRow">
            <input className="darkInput" placeholder={hl(lang, "Yakınlık")} value={form.relation || ""} onChange={(e) => set("relation", e.target.value)} />
            <input className={healthValidation.fieldClass("phone", "darkInput")} data-required-field={`health-${type}-phone`} aria-invalid={healthValidation.hasError("phone")} inputMode="tel" placeholder={hl(lang, "Telefon")} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <textarea className="darkInput" rows={2} placeholder={hl(lang, "Kritik alerjiler")} value={form.criticalAllergies || ""} onChange={(e) => set("criticalAllergies", e.target.value)} />
          <textarea className="darkInput" rows={2} placeholder={hl(lang, "Kronik hastalıklar / kritik ilaçlar")} value={form.criticalConditions || form.note || ""} onChange={(e) => set("criticalConditions", e.target.value)} />
        </>
      )}

      {type === "note" && (
        <>
          <input className={healthValidation.fieldClass("title", "darkInput")} data-required-field={`health-${type}-title`} aria-invalid={healthValidation.hasError("title")} placeholder={hl(lang, "Başlık")} value={form.title || ""} onChange={(e) => set("title", e.target.value)} />
          <div className="formRow">
            <DatalistInput id="noteCategoryOptions" placeholder={hl(lang, "Kategori")} value={form.category || ""} onChange={(v) => set("category", v)} options={NOTE_CATEGORY_OPTIONS} lang={lang} />
            <input className="darkInput" type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} />
          </div>
          <textarea className={healthValidation.fieldClass("note", "darkInput")} data-required-field={`health-${type}-note`} aria-invalid={healthValidation.hasError("note")} rows={3} placeholder={hl(lang, "Sağlık notu")} value={form.note || ""} onChange={(e) => set("note", e.target.value)} />
        </>
      )}

      {type === "report" && (
        <>
          <input className={healthValidation.fieldClass("title", "darkInput")} data-required-field={`health-${type}-title`} aria-invalid={healthValidation.hasError("title")} placeholder={hl(lang, "Tahlil / rapor adı")} value={form.title || ""} onChange={(e) => set("title", e.target.value)} />
          <div className="formRow">
            <DatalistInput id="reportTypeOptions" placeholder={hl(lang, "Rapor tipi")} value={form.reportType || ""} onChange={(v) => set("reportType", v)} options={REPORT_TYPE_OPTIONS} lang={lang} />
            <input className="darkInput" type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} />
          </div>
          <div className="formRow">
            <input className="darkInput" placeholder={hl(lang, "Kurum / laboratuvar")} value={form.institution || ""} onChange={(e) => set("institution", e.target.value)} />
            <input className="darkInput" placeholder={hl(lang, "Doktor")} value={form.doctor || ""} onChange={(e) => set("doctor", e.target.value)} />
          </div>
          <textarea className="darkInput" rows={2} placeholder={hl(lang, "Sonuç özeti")} value={form.resultSummary || form.note || ""} onChange={(e) => set("resultSummary", e.target.value)} />

          <div className="dosyaSecArea" onClick={() => inputRef.current?.click()}>
            {dosya ? (
              <div className="dosyaSecili"><span style={{ fontSize: "28px" }}>📄</span><div style={{ flex: 1, minWidth: 0 }}><div className="dosyaSeciliAd">{dosya.name}</div><div className="dosyaSeciliBoyut">{(dosya.size / (1024 * 1024)).toFixed(2)} MB</div></div><button type="button" onClick={(e) => { e.stopPropagation(); setDosya(null); if (inputRef.current) inputRef.current.value = ""; }} style={{ background: "none", border: "none", color: "#f87171", fontSize: "18px", cursor: "pointer" }}>×</button></div>
            ) : (
              <div className="dosyaSecPlaceholder"><span style={{ fontSize: "36px" }}>📂</span><span>{hl(lang, "Dosya seçmek için dokun")}</span><span className="dosyaSecAlt">PDF, JPG, PNG, DOCX</span></div>
            )}
          </div>
          <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx" style={{ display: "none" }} onChange={(e) => setDosya(e.target.files[0] || null)} />
        </>
      )}

      <FormValidationNotice errors={healthValidation.errors} labels={requiredLabels} />
      <div className="formRow">
        <AsyncActionButton
          className="primaryButton"
          style={{ background: color }}
          onAction={save}
          externalBusy={submitting}
          busyText={initialData ? hl(lang, "Kaydediliyor...") : hl(lang, "Ekleniyor...")}
          disabled={submitting}
        >
          {t("health.save")}
        </AsyncActionButton>
        <button className="darkSecondaryBtn" type="button" onClick={onCancel} disabled={submitting}>{t("health.cancel")}</button>
      </div>
    </div>
  );
}

function HealthMiniRecord({ record, color, onToggle, onDoseAction, onDelete, onEdit, onOpenAttachment }) {
  const { t, lang } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const today = bugunKey();

  let statusText = t("health.record.recordCount");
  let statusClass = "";

  if (record.type === "medicine") {
    const stats = getMedicineTodayStats(record);
    if (stats.total === 0) { statusText = `○ ${t("health.status.passive")}`; statusClass = "waiting"; }
    else if (stats.taken === stats.total) { statusText = `● ${t("health.summary.todayDosesDone")}`; statusClass = "done"; }
    else if (stats.missed > 0) { statusText = `● ${stats.missed} ${t("health.status.pending")}`; statusClass = "late"; }
    else { statusText = `○ ${stats.pending} ${t("health.record.recordCount")}`; statusClass = "waiting"; }
  } else if (record.type === "allergy") {
    statusText = hl(lang, record.level || t("health.notSpecified"));
    statusClass = ["Ciddi", "Acil"].includes(record.level) ? "late" : "waiting";
  } else if (record.type === "appointment") {
    const past = record.date && record.date < bugunKey();
    statusText = past ? t("health.status.passive") : t("calendar.upcoming");
    statusClass = past ? "waiting" : "done";
  } else if (record.type === "vaccine") {
    const late = !record.done && record.plannedDate && record.plannedDate < bugunKey();
    statusText = record.done ? t("health.status.dotDone") : late ? t("health.status.dotLate") : t("health.status.dotPending");
    statusClass = record.done ? "done" : late ? "late" : "waiting";
  } else if (record.done !== undefined) {
    statusText = record.done ? t("health.status.dotCompleted") : t("health.status.dotPending");
    statusClass = record.done ? "done" : "waiting";
  } else if (record.active !== undefined) {
    statusText = record.active === false ? t("health.status.dotPassive") : t("health.status.dotActive");
    statusClass = record.active === false ? "waiting" : "done";
  }

  const times = normalizeMedicineTimes(record.times || []);
  const stats = record.type === "medicine" ? getMedicineTodayStats(record) : null;
  const detailRows = recordDetailRows(record, t, lang);

  return (
    <div className={`healthMiniRecord ${record.type === "medicine" ? "medicineDoseCard" : ""} healthType-${record.type}`} onClick={() => { if (!menuOpen && record.type === "medicine" && onToggle) onToggle(record); }} style={{ borderColor: `${color}44` }}>
      {(onEdit || onDelete) && <button className="healthMiniMenuBtn" onClick={(e) => { e.stopPropagation(); setMenuOpen((p) => !p); }}>⋯</button>}

      {menuOpen && (onEdit || onDelete) && (
        <div className="healthMiniMenu" onClick={(e) => e.stopPropagation()}>
          {onEdit && <button onClick={() => { setMenuOpen(false); onEdit(record); }}>{hl(lang, "Düzenle")}</button>}
          {onDelete && <button className="danger" onClick={() => { setMenuOpen(false); onDelete(record); }}>{hl(lang, "Sil")}</button>}
        </div>
      )}

      <ExpandableHealthText className="healthMiniTitle">{hl(lang, getRecordTitle(record, t))}</ExpandableHealthText>
      <ExpandableHealthText className="healthMiniSub">{hl(lang, getRecordSub(record, t, lang))}</ExpandableHealthText>

      {record.type !== "medicine" && detailRows.length > 0 && (
        <div className="healthDetailRows">
          {detailRows.slice(0, 5).map(([label, value]) => (
            <div key={label} className="healthDetailRow"><span>{label}</span><b><ExpandableHealthText>{hl(lang, value)}</ExpandableHealthText></b></div>
          ))}
        </div>
      )}

      {record.type === "report" && record.fileUrl && (
        <button type="button" className="healthReportThumb" onClick={(event) => { event.stopPropagation(); onOpenAttachment?.(record); }}>
          <span className="healthReportThumbPreview">
            {record.previewUrl ? <RenewableFileImage src={record.previewUrl} alt="" decoding="async" /> : <span>{dosyaIkonu(record.fileType || record.fileName || "pdf")}</span>}
          </span>
          <span className="healthReportThumbText">
            <b>{record.fileName || record.title || (lang === "en" ? "Report file" : "Rapor dosyası")}</b>
            <small>{lang === "en" ? "Tap to preview" : "Önizlemek için dokun"}</small>
          </span>
          <span className="healthReportThumbArrow" aria-hidden="true">›</span>
        </button>
      )}

      {record.type === "emergency" && record.phone && (
        <a className="healthCallBtn" href={`tel:${record.phone}`} onClick={(e) => e.stopPropagation()}>📞 {hl(lang, "Ara")}</a>
      )}

      {record.type === "medicine" && stats && (
        <div className="medicineProgressWrap">
          <div className="medicineProgressTop"><span>{hl(lang, "Bugün")}</span><b>{stats.taken}/{stats.total || 0} {hl(lang, "doz")}</b></div>
          <div className="medicineProgressBar"><div style={{ width: `${stats.progress}%`, background: color }} /></div>
        </div>
      )}

      {record.type === "medicine" && times.length > 0 && (
        <div className="medicineDoseList">
          {times.map((time) => {
            const info = doseStatusInfo(record, time, today);
            return (
              <div key={time} className={`medicineDoseRow ${info.className}`}>
                <span className="medicineDoseTime">{time}</span>
                <span className="medicineDoseState">{info.icon} {info.label}</span>
                <div className="medicineDoseActions" onClick={(e) => e.stopPropagation()}>
                  <button type="button" onClick={() => onDoseAction?.(record, time, "taken")}>{hl(lang, "Aldım")}</button>
                  <button type="button" onClick={() => onDoseAction?.(record, time, "snoozed")}>
                   {lang === "en" ? "10 min" : "10 dk"}
                  </button>
                  <button type="button" onClick={() => onDoseAction?.(record, time, "skipped")}>{hl(lang, "Atla")}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={`healthStatusBadge ${statusClass}`}>{statusText}</div>
    </div>
  );
}


function HealthRecordSlider({ records, color, onToggle, onDoseAction, onDelete, onEdit }) {
  const { lang } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [attachmentRecord, setAttachmentRecord] = useState(null);
  const [secureViewerItem, setSecureViewerItem] = useState(null);
  const [downloadingAttachment, setDownloadingAttachment] = useState(false);
  const viewerOpenTimerRef = useRef(null);
  const trackRef = useRef(null);
  const sortedRecords = useMemo(() => sortRecordsNewestFirst(records), [records]);

  useEffect(() => {
    setActiveIndex(0);
    if (trackRef.current) trackRef.current.scrollTo({ left: 0, behavior: "auto" });
  }, [sortedRecords.length]);

  const handleScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const width = el.clientWidth || 1;
    const next = Math.round(el.scrollLeft / width);
    setActiveIndex(Math.min(Math.max(next, 0), Math.max(sortedRecords.length - 1, 0)));
  };

  const goToSlide = (index) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
    setActiveIndex(index);
  };

  const attachmentName = (record) => record?.fileName || record?.title || (lang === "en" ? "Health report" : "Sağlık raporu");
  const attachmentMime = (record) => record?.fileType || record?.mimeType || "application/octet-stream";

  useEffect(() => () => {
    if (viewerOpenTimerRef.current) window.clearTimeout(viewerOpenTimerRef.current);
  }, []);

  const openAttachmentViewer = (record) => {
    if (!record?.fileUrl) return;
    const viewerItem = {
      url: record.fileUrl,
      fileName: attachmentName(record),
      mimeType: attachmentMime(record),
      title: record.title,
    };
    if (viewerOpenTimerRef.current) window.clearTimeout(viewerOpenTimerRef.current);
    setAttachmentRecord(null);
    setSecureViewerItem(null);
    // iOS WKWebView'de alttaki popup'ın compositing/focus katmanı tamamen
    // kaldırıldıktan sonra tam ekran viewer ayrı bir render turunda açılır.
    viewerOpenTimerRef.current = window.setTimeout(() => {
      viewerOpenTimerRef.current = null;
      setSecureViewerItem(viewerItem);
    }, 60);
  };

  const downloadAttachment = async (record) => {
    if (!record?.fileUrl || downloadingAttachment) return;
    setDownloadingAttachment(true);
    try {
      const blob = await fetchBelgeBlob(record.fileUrl);
      const finalMime = blob.type || (String(attachmentMime(record)).includes("/") ? attachmentMime(record) : "application/octet-stream");
      const finalName = safeDosyaAdi(attachmentName(record), attachmentMime(record) || finalMime, "saglik-raporu");
      const nativeBridge = window?.webkit?.messageHandlers?.aileNativeBridge;
      if (nativeBridge) {
        nativeBridge.postMessage({ action: "shareFile", base64: await blobToBase64(blob), mimeType: finalMime, fileName: finalName });
        return;
      }
      if (navigator?.canShare && window.File) {
        const file = new File([blob], finalName, { type: finalMime });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: finalName });
          return;
        }
      }
      if (isAndroidPlatform() && await androidDosyaPaylas(blob, finalName, finalName)) return;
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = finalName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch (_) {
      await appAlert(lang === "en" ? "File could not be downloaded." : "Dosya indirilemedi.");
    } finally {
      setDownloadingAttachment(false);
    }
  };

  if (!sortedRecords.length) return null;

  return (
    <>
    <div className="medicineSliderShell healthRecordSliderShell">
      <div className="medicineSliderTrack healthRecordSliderTrack" ref={trackRef} onScroll={handleScroll}>
        {sortedRecords.map((record) => (
          <div className="medicineSlide healthRecordSlide" key={record.id}>
            <HealthMiniRecord
              record={record}
              color={color}
              onToggle={onToggle}
              onDoseAction={onDoseAction}
              onDelete={onDelete}
              onEdit={onEdit}
              onOpenAttachment={setAttachmentRecord}
            />
          </div>
        ))}
      </div>

      {sortedRecords.length > 1 && (
        <div className="medicineSliderDots healthRecordSliderDots" aria-label={lang === "en" ? "Health records slide indicator" : "Sağlık kayıtları slayt göstergesi"}>
          {sortedRecords.map((record, index) => (
            <button
              type="button"
              key={record.id || index}
              className={index === activeIndex ? "active" : ""}
              onClick={() => goToSlide(index)}
              aria-label={lang === "en" ? `${index + 1}. health record` : `${index + 1}. sağlık kaydı`}
            />
          ))}
        </div>
      )}
    </div>
    {attachmentRecord && (
      <div className="modalOverlay" onClick={() => setAttachmentRecord(null)}>
        <div className="darkModal belgeDetayModal healthReportAttachmentModal" onClick={(event) => event.stopPropagation()}>
          <div className="darkModalHeader">
            <span className="darkModalTitle">{dosyaIkonu(attachmentMime(attachmentRecord) || attachmentName(attachmentRecord))} {attachmentName(attachmentRecord)}</span>
            <button className="ayarKapatBtn" onClick={() => setAttachmentRecord(null)}>×</button>
          </div>
          <button type="button" className="healthReportLargePreview" onClick={() => openAttachmentViewer(attachmentRecord)}>
            {attachmentRecord.previewUrl ? <RenewableFileImage src={attachmentRecord.previewUrl} alt={attachmentName(attachmentRecord)} /> : <span>{dosyaIkonu(attachmentMime(attachmentRecord) || attachmentName(attachmentRecord))}</span>}
            <small>{lang === "en" ? "Open full preview" : "Tam önizlemeyi aç"}</small>
          </button>
          <div className="belgeAksiyonlar">
            <button type="button" className="belgeAksiyonBtn primary" onClick={() => openAttachmentViewer(attachmentRecord)}><MediaActionIcon type="view" /> {lang === "en" ? "View" : "Görüntüle"}</button>
            <button type="button" className="belgeAksiyonBtn secondary" disabled={downloadingAttachment} onClick={() => downloadAttachment(attachmentRecord)}>{downloadingAttachment ? <MediaActionLoading /> : <MediaActionIcon type="download" />} {lang === "en" ? "Download" : "İndir"}</button>
            <button type="button" className="belgeAksiyonBtn danger" onClick={async () => { if (await onDelete?.(attachmentRecord)) setAttachmentRecord(null); }}><MediaActionIcon type="delete" /> {lang === "en" ? "Delete" : "Sil"}</button>
          </div>
        </div>
      </div>
    )}
    <SecureMediaViewer open={!!secureViewerItem} item={secureViewerItem} onClose={() => setSecureViewerItem(null)} lang={lang} />
    </>
  );
}

function HealthDocumentSlider({ documents, color, user, yoneticiMi, belgeSil, canManage = false }) {
  const { t, lang } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [secureViewerItem, setSecureViewerItem] = useState(null);
  const trackRef = useRef(null);
  const sortedDocuments = useMemo(() => sortDocumentsNewestFirst(documents), [documents]);

  useEffect(() => {
    setActiveIndex(0);
    if (trackRef.current) trackRef.current.scrollTo({ left: 0, behavior: "auto" });
  }, [sortedDocuments.length]);

  useEffect(() => {
    setPreviewError(false);
  }, [selectedDoc?.id, selectedDoc?.dosyaUrl]);

  const handleScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const width = el.clientWidth || 1;
    const next = Math.round(el.scrollLeft / width);
    setActiveIndex(Math.min(Math.max(next, 0), Math.max(sortedDocuments.length - 1, 0)));
  };

  const goToSlide = (index) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
    setActiveIndex(index);
  };

  const handleDownload = async (doc) => {
    if (!doc || downloading) return;
    setDownloading(true);
    try {
      const blob = await fetchBelgeBlob(doc.dosyaUrl);
      const finalMime = blob.type || (String(doc.dosyaTipi || "").includes("/") ? doc.dosyaTipi : "application/octet-stream");
      const finalName = safeDosyaAdi(doc.dosyaAdi || doc.ad, doc.dosyaTipi || finalMime, doc.ad || "saglik-belgesi");
      const nativeBridge = window?.webkit?.messageHandlers?.aileNativeBridge;

      if (nativeBridge) {
        const base64 = await blobToBase64(blob);
        nativeBridge.postMessage({ action: "shareFile", base64, mimeType: finalMime, fileName: finalName });
        return;
      }

      if (navigator?.canShare && window.File) {
        const file = new File([blob], finalName, { type: finalMime });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: finalName });
          return;
        }
      }

      if (isAndroidPlatform()) {
        const shared = await androidDosyaPaylas(blob, finalName, finalName);
        if (shared) return;
      }

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    } catch (_) {
      await appAlert(lang === "en" ? "File could not be downloaded." : "Dosya indirilemedi.");
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!doc || !belgeSil) return;
    const ok = await appConfirm(lang === "en" ? "Delete this health document?" : "Bu sağlık belgesi silinsin mi?");
    if (!ok) return;
    await belgeSil({ belgeId: doc.id, belge: doc });
    setSelectedDoc(null);
  };

  if (!sortedDocuments.length) return null;

  return (
    <>
      <div className="healthLinkedDocsTitle">📎 {lang === "en" ? "Health documents" : "Sağlık belgeleri"}</div>
      <div className="medicineSliderShell healthRecordSliderShell healthDocumentSliderShell">
        <div className="medicineSliderTrack healthRecordSliderTrack" ref={trackRef} onScroll={handleScroll}>
          {sortedDocuments.map((doc) => (
            <div className="medicineSlide healthRecordSlide" key={doc.id}>
              <button
                type="button"
                className="healthMiniRecord healthDocumentLink healthDocumentSlideCard"
                onClick={() => setSelectedDoc(doc)}
                style={{ borderColor: `${color}44` }}
              >
                <div className="healthDocumentIcon">{dosyaIkonu(doc.dosyaTipi || doc.dosyaAdi)}</div>
                <div className="healthMiniTitle">{doc.ad || doc.dosyaAdi || hl(lang, "Sağlık Belgesi")}</div>
                <div className="healthMiniSub">{doc.dosyaAdi || doc.aciklama || t("health.vaultDoc")}</div>
                <div className="healthDocumentMeta">
                  <span>{hl(lang, "Belge Kasası")}</span>
                  {doc.dosyaBoyutu ? <span>{boyutFormatla(doc.dosyaBoyutu)}</span> : null}
                </div>
                {doc.aciklama && <ExpandableHealthText className="healthDocumentNote">{doc.aciklama}</ExpandableHealthText>}
              </button>
            </div>
          ))}
        </div>

        {sortedDocuments.length > 1 && (
          <div className="medicineSliderDots healthRecordSliderDots" aria-label={lang === "en" ? "Health document slide indicator" : "Sağlık belgesi slayt göstergesi"}>
            {sortedDocuments.map((doc, index) => (
              <button
                type="button"
                key={doc.id || index}
                className={index === activeIndex ? "active" : ""}
                onClick={() => goToSlide(index)}
                aria-label={lang === "en" ? `${index + 1}. health document` : `${index + 1}. sağlık belgesi`}
              />
            ))}
          </div>
        )}
      </div>

      {selectedDoc && (
        <div className="modalOverlay" onClick={() => setSelectedDoc(null)}>
          <div className="darkModal belgeDetayModal healthDocumentModal" onClick={(e) => e.stopPropagation()}>
            <div className="darkModalHeader">
              <span className="darkModalTitle">{dosyaIkonu(selectedDoc.dosyaTipi || selectedDoc.dosyaAdi)} {selectedDoc.ad || selectedDoc.dosyaAdi}</span>
              <button className="ayarKapatBtn" onClick={() => setSelectedDoc(null)}>×</button>
            </div>

            <button type="button" className="mediaDetailPreviewTrigger" onClick={() => setSecureViewerItem({ url: selectedDoc.dosyaUrl, fileName: selectedDoc.dosyaAdi || selectedDoc.ad, mimeType: selectedDoc.dosyaTipi, title: selectedDoc.ad || selectedDoc.dosyaAdi })} aria-label={lang === "en" ? "Open document preview" : "Belge önizlemesini aç"}>
            {["jpg", "jpeg", "png", "gif", "webp"].includes(normalizeDosyaTipi(selectedDoc.dosyaTipi || selectedDoc.dosyaAdi)) && !previewError ? (
              <RenewableFileImage
                src={selectedDoc.previewUrl || selectedDoc.dosyaUrl}
                alt={selectedDoc.ad || selectedDoc.dosyaAdi || ""}
                className="belgeOnizleme"
                decoding="async"
                onError={() => setPreviewError(true)}
              />
            ) : (
              <div className="belgeDosyaKart">
                <span style={{ fontSize: "42px" }}>{dosyaIkonu(selectedDoc.dosyaTipi || selectedDoc.dosyaAdi)}</span>
                <span className="belgeDosyaAd">{previewError ? (lang === "en" ? "Preview could not be loaded." : "Önizleme yüklenemedi.") : selectedDoc.dosyaAdi}</span>
                <span className="belgeDosyaBoyut">{boyutFormatla(selectedDoc.dosyaBoyutu)}</span>
              </div>
            )}
            </button>

            <div className="belgeMetaList">
              <div className="belgeMetaRow"><span className="belgeMetaLbl">{lang === "en" ? "Category" : "Kategori"}</span><span className="belgeMetaVal">🏥 {hl(lang, "Sağlık")}</span></div>
              {selectedDoc.sahipAd && <div className="belgeMetaRow"><span className="belgeMetaLbl">{lang === "en" ? "Owner" : "Sahip"}</span><span className="belgeMetaVal">{selectedDoc.sahipAd}</span></div>}
              {selectedDoc.yukleyenAd && <div className="belgeMetaRow"><span className="belgeMetaLbl">{lang === "en" ? "Uploader" : "Yükleyen"}</span><span className="belgeMetaVal">{selectedDoc.yukleyenAd}</span></div>}
              {selectedDoc.aciklama && <div className="belgeMetaRow"><span className="belgeMetaLbl">📝 {lang === "en" ? "Description" : "Açıklama"}</span><span className="belgeMetaVal"><ExpandableHealthText>{selectedDoc.aciklama}</ExpandableHealthText></span></div>}
            </div>

            <div className="belgeAksiyonlar">
              <button type="button" className="belgeAksiyonBtn primary" onClick={() => setSecureViewerItem({
                url: selectedDoc.dosyaUrl,
                fileName: selectedDoc.dosyaAdi || selectedDoc.ad,
                mimeType: selectedDoc.dosyaTipi,
                title: selectedDoc.ad || selectedDoc.dosyaAdi,
              })}><MediaActionIcon type="view" /> {lang === "en" ? "View" : "Görüntüle"}</button>
              <button className="belgeAksiyonBtn secondary" onClick={() => handleDownload(selectedDoc)} disabled={downloading}>
                {downloading ? <MediaActionLoading /> : <MediaActionIcon type="download" />} {lang === "en" ? "Download" : "İndir"}
              </button>
              {(canManage || yoneticiMi || selectedDoc.yukleyenUid === user?.uid) && belgeSil && (
                <button className="belgeAksiyonBtn danger" onClick={() => handleDelete(selectedDoc)}><MediaActionIcon type="delete" /> {hl(lang, "Sil")}</button>
              )}
            </div>
          </div>
        </div>
      )}
      <SecureMediaViewer open={!!secureViewerItem} item={secureViewerItem} onClose={() => setSecureViewerItem(null)} lang={lang} />
    </>
  );
}

function HealthSummaryDashboard({ uye, records, belgeler }) {
  const { t, lang } = useLanguage();
  const medicines = records.filter((r) => r.type === "medicine");
  const appointments = sortByDateAsc(records.filter((r) => r.type === "appointment" && (!r.date || r.date >= bugunKey())))[0];
  const allergyCount = records.filter((r) => r.type === "allergy").length;
  const seriousAllergies = records.filter((r) => r.type === "allergy" && ["Ciddi", "Acil"].includes(r.level));
  const conditions = records.filter((r) => r.type === "condition" && r.active !== false);
  const emergency = records.find((r) => r.type === "emergency");
  const measurement = sortByDateAsc(records.filter((r) => r.type === "measurement"), "date").reverse()[0];
  const totalDose = medicines.reduce((sum, r) => sum + getMedicineTodayStats(r).total, 0);
  const takenDose = medicines.reduce((sum, r) => sum + getMedicineTodayStats(r).taken, 0);
  const healthProfile = uye?.healthProfile || {};

  const cards = [
    [`🩸 ${t("profile.bloodType")}`, emergency?.bloodType || healthProfile.bloodType || t("health.notSpecified")],
    [`💊 ${t("health.card.medicine")}`, totalDose ? `${takenDose}/${totalDose}` : t("health.summary.noMedicine")],
    [t("health.record.nextAppt"), appointments?.date ? `${formatDateTR(appointments.date, lang)}${appointments.time ? ` · ${appointments.time}` : ""}` : t("health.record.noAppointment")],
    [`⚠️ ${t("health.card.allergy")}`, seriousAllergies.length ? `${seriousAllergies.length} ${t("health.summary.chronic")}` : `${allergyCount} ${t("health.record.recordCount")}`],
    [`🫀 ${t("health.card.condition")}`, conditions.length ? `${conditions.length} ${t("health.status.active")}` : t("health.record.noAppointment")],
    [t("health.record.lastMeasurement"), measurement ? getRecordSub(measurement, t, lang) : t("health.record.noMeasurement")],
    [t("health.record.reports"), `${records.filter((r) => r.type === "report").length + belgeler.length} ${t("health.record.recordCount")}`],
    [`🚨 ${t("health.card.emergency")}`, emergency?.contactName || healthProfile.emergencyPerson || t("health.notSpecified")],
  ];

  return (
    <div className="healthSummaryDashboard">
      <div className="healthSummaryHero">
        <div>
          <span>{t("health.summary.label")}</span>
          <h3>{uye.ad} {uye.soyad}</h3>
          <p>{records.length} {t("health.record.recordCount")} · {medicines.length} {t("health.summary.activeMedicine")} · {allergyCount} {t("health.card.allergy")}</p>
        </div>
        <div
          className={`healthSummaryScore${totalDose ? "" : " empty"}`}
          aria-label={totalDose ? `${Math.round((takenDose / totalDose) * 100)}%` : t("health.summary.noMedicine")}
        >
          {totalDose ? Math.round((takenDose / totalDose) * 100) : "—"}
          {totalDose > 0 && <small>%</small>}
        </div>
      </div>
      <div className="healthSummaryGrid">
        {cards.map(([label, value]) => <div key={label} className="healthSummaryTile"><span>{label}</span><b><ExpandableHealthText>{value}</ExpandableHealthText></b></div>)}
      </div>
      {seriousAllergies.length > 0 && (
        <div className="healthCriticalStrip">🚨 {hl(lang, "Kritik alerji")}: <ExpandableHealthText>{seriousAllergies.map((a) => hl(lang, a.name || a.title)).join(", ")}</ExpandableHealthText></div>
      )}
    </div>
  );
}

function HealthDetailPage({
  uye,
  records,
  user,
  familyId,
  yoneticiMi,
  uyeler = [],
  belgeler = [],
  belgeYukle,
  belgeSil,
  onGeri,
  transitionClass = "idle",
  isPremium = false,
  premiumConfig = {},
  onPremiumLimitHit,
  onOzelTakvimAc,
}) {
  const { t, lang } = useLanguage();
  const healthCalendarCategory = "Sağlık";
  // HEALTH_CARDS bu component içinde t() ile oluşturulur
  const HEALTH_CARDS = HEALTH_CARD_IDS.map(c => ({ ...c, title: t(`health.card.${c.id}`) }));

  // Özel Takvimim kartı görünürlük kontrolü
  const _SUPER_ADMIN_UID = "eRmq1zfRhKM4LH2SrGG8G1Q5tEG2";
  const _superAdminKendi = user?.uid === _SUPER_ADMIN_UID && uye.uid === user?.uid;
  const _cins = (uye.cinsiyet || "").toLowerCase();
  const _rol  = uye.rol || "";
  const _kadinMi = _cins === "kadın" || _cins === "kadin" || _cins === "female" || _cins === "woman" || _rol === "Anne" || _rol === "Nene" || _rol === "Teyze";
  const _cocukMu = ["Çocuk","Bebek"].includes(_rol);
  const _cinsYok = !_cins && !["Anne","Nene","Teyze"].includes(_rol);
  const _ozelTakvimGoster = _superAdminKendi || (_kadinMi && !_cocukMu && !_cinsYok);

  // Özel Takvimim kartını Sağlık Özeti'nin yanına (2. pozisyona) enjekte et
  const _ozelKart = { id: "ozel_takvim", icon: healthPrivateCalendarAsset, color: "#ef476f", isSpecial: true,
    title: lang === "en" ? "My Private Cycle" : "Özel Takvimim",
    subText: lang === "en" ? "Cycle & women's health" : "Regl & kadın sağlığı" };
  // Kart tüm kullanıcılara görünür (kendi kartında). Yalnızca cinsiyeti kadın olanlar açabilir.
  const _kendiKarti = uye.uid === user?.uid;
  const cardsWithOzel = _kendiKarti
    ? [HEALTH_CARDS[0], _ozelKart, ...HEALTH_CARDS.slice(1)]
    : HEALTH_CARDS;

  const detailPageRef = useRef(null);
  useHealthKeyboardViewport(detailPageRef);

  useEffect(() => {
    detailPageRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);
  const [activeCard, setActiveCard] = useState(null);
  const [addingType, setAddingType] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);

  const actorMember = uyeler.find((member) => memberUid(member) === String(user?.uid || ""));
  const canManage = canManageChildMember({ actor: actorMember, target: uye, isFamilyAdmin: yoneticiMi });
  const memberRecords = records.filter((r) => r.memberUid === uye.uid);

  const saglikBelgeleri = belgeler.filter((b) => {
    if (b.tip !== "kisisel") return false;
    if (b.sahipUid !== uye.uid) return false;
    const kategori = String(b.kategori || "").toLowerCase();
    return kategori === "🏥 sağlık" || kategori === "🏥 health" || kategori === "sağlık" || kategori === "health";
  });

  const limits = premiumConfig?.limits || {};

  const addRecord = async (type, form) => {
    if (!canManage) {
      appAlert(t("health.noPermission"));
      return;
    }
    if (!isPremium) {
      const kisiKayit = memberRecords.length;
      const maxKayit = limits.saglikKayitKisi ?? 10;
      if (kisiKayit >= maxKayit) {
        onPremiumLimitHit && onPremiumLimitHit(t("health.premium.title"), kisiKayit, maxKayit, [t("health.premium.b1"), t("health.premium.b2"), t("health.premium.b3"), t("health.premium.b4")]);
        return;
      }
    }

    const actorName = (() => {
      const me = uyeler.find((u) => u.uid === user?.uid);
      return (me ? `${me.ad || ""} ${me.soyad || ""}`.trim() : "") || user?.displayName || t("health.userFallback");
    })();

    const base = {
      familyId,
      memberUid: uye.uid,
      memberName: `${uye.ad} ${uye.soyad}`,
      type,
      createdBy: user?.uid,
      ekleyen: actorName,
      ekleyenUid: user?.uid,
      calendarColor: uye.calendarColor || "#8b5cf6",
      memberColor: uye.calendarColor || "#8b5cf6",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    let data = { ...form };
    const secilenDosya = form.dosya || null;
    delete data.dosya;

    if (type === "medicine") {
      const times = normalizeMedicineTimes(form.timesText || "");
      if (times.length === 0) {
        appAlert(t("health.invalidMedTime"));
        return;
      }

      data = {
        name: form.name || t("health.card.medicine"),
        dose: form.dose || "",
        note: form.note || "",
        times,
        startDate: form.startDate || bugunKey(),
        endDate: form.endDate || "",
        doseLogs: {},
        takenDates: {},
        reminderEnabled: true,
      };
    }


    if (type === "appointment") {
      data = {
        doctor: form.doctor || hl(lang, "Doktor / Hastane belirtilmedi"),
        branch: form.branch || t("health.appointment.default"),
        appointmentType: form.appointmentType || (lang === "en" ? "Examination" : "Muayene"),
        date: form.date || bugunKey(),
        time: form.time || "",
        location: form.location || "",
        note: form.note || "",
        addToCalendar: form.addToCalendar !== false,
        createTask: !!form.createTask,
      };
    }

    if (type === "measurement") {
      const normalizedValue = form.measurementType === "Tansiyon"
        ? `${form.systolic || "-"}/${form.diastolic || "-"}`
        : normalizeBodyMeasurementInput(form.measurementType, form.value);
      data = {
        measurementType: form.measurementType,
        systolic: form.systolic || "",
        diastolic: form.diastolic || "",
        value: normalizedValue,
        unit: bodyMeasurementUnit(form.measurementType) || form.unit || (form.measurementType === "Ateş" || form.measurementType === "Fever" ? "°C" : ""),
        context: form.context || "",
        date: form.date || bugunKey(),
        note: form.note || "",
      };
    }

    if (type === "allergy") {
      data = { name: form.name || t("health.card.allergy"), allergyType: form.allergyType || hl(lang, "Diğer"), level: form.level || hl(lang, "Belirtilmedi"), symptoms: form.symptoms || "", action: form.action || "", avoid: form.avoid || "", note: form.note || form.avoid || "" };
    }

    if (type === "condition") {
      data = { title: form.title || t("health.card.condition"), diagnosisDate: form.diagnosisDate || "", doctor: form.doctor || "", followUp: form.followUp || "", medicines: form.medicines || "", note: form.note || form.medicines || "", active: form.active !== false };
    }

    if (type === "vaccine") {
      data = { title: form.title || t("health.card.vaccine"), doseNo: form.doseNo || "", plannedDate: form.plannedDate || bugunKey(), done: !!form.done, doneDate: form.doneDate || "", nextDoseDate: form.nextDoseDate || "", note: form.note || "" };
    }

    if (type === "emergency") {
      data = { title: form.contactName || form.title || t("health.card.emergency"), bloodType: form.bloodType || "", contactName: form.contactName || form.title || "", relation: form.relation || "", phone: form.phone || "", criticalAllergies: form.criticalAllergies || "", criticalConditions: form.criticalConditions || "", criticalMedicines: form.criticalMedicines || "", note: form.note || form.criticalConditions || "" };
    }

    if (type === "note") {
      data = { title: form.title || t("health.card.note"), category: form.category || hl(lang, "Genel"), date: form.date || bugunKey(), note: form.note || "" };
    }

    if (type === "report") {
      data = { title: form.title || (secilenDosya?.name || t("health.defaultReport")), reportType: form.reportType || hl(lang, "Diğer"), date: form.date || bugunKey(), institution: form.institution || "", doctor: form.doctor || "", resultSummary: form.resultSummary || "", note: form.note || form.resultSummary || "" };
    }

    if (type === "report" && secilenDosya && belgeYukle) {
      const uploadedDoc = await belgeYukle({
        dosya: secilenDosya,
        ad: data.title || secilenDosya.name,
        aciklama: data.note || "",
        kategori: `🏥 ${t("nav.health")}`,
        gecerlilikTarihi: null,
        tip: "kisisel",
        sahipUid: uye.uid,
        sahipAd: `${uye.ad} ${uye.soyad}`,
        gorebilecekler: [],
        storageType: "health",
      });
      // Yüklenen belgenin dosya bilgisini sağlık kaydına da bağlıyoruz —
      // aksi halde dosya "Belgeler" sayfasında görünür ama sağlık kaydının
      // kendisinden erişilemez/silinemez kalırdı.
      if (uploadedDoc) {
        data.fileKey = uploadedDoc.publicId || uploadedDoc.fileKey || null;
        data.fileUrl = uploadedDoc.url || uploadedDoc.fileUrl || null;
        data.fileName = uploadedDoc.fileName || secilenDosya.name || null;
        data.fileType = uploadedDoc.fileType || secilenDosya.type || null;
        data.fileSize = Number(secilenDosya.size || 0) || null;
        data.linkedDocumentId = uploadedDoc.id || null;
      }
    }

    const savedRecord = await saglikKaydiEkleService({
      familyId,
      payload: {
        ...base,
        ...data,
        linkedDocumentCategory: type === "report" ? "🏥 Sağlık" : null,
        linkedDocumentId: data.linkedDocumentId || null,
      },
    });

    const savedRecordId = savedRecord?.id || savedRecord?.firebaseId || "";

    await createActivity({
      familyId,
      type: "health_added",
      category: "health",
      actor: getActivityActor({ user, name: (() => { const me = uyeler.find(u => u.uid === user?.uid); return me ? `${me.ad || ""} ${me.soyad || ""}`.trim() : user?.displayName || ""; })(), avatar: uye?.avatar || null }),
      payload: { recordName: data.title || data.doctor || data.branch || type, memberName: `${uye.ad} ${uye.soyad}`, recordType: type },
      targetPage: "saglik",
      targetId: savedRecordId,
      visibility: "private",
    });


    setAddingType(null);
    setEditingRecord(null);
  };

  const toggleMedicineTaken = async (medicine) => {
    if (!canManage) return;

    const times = normalizeMedicineTimes(medicine.times || []);
    if (times.length === 0) return;

    const stats = getMedicineTodayStats(medicine);
    const nextTime = stats.nextTime || times[0];
    await updateMedicineDose(medicine, nextTime, "taken");
  };

  const updateMedicineDose = async (medicine, time, action) => {
    if (!canManage || !medicine?.id || !time) return;

    const today = bugunKey();
    const mevcutGun = medicine.doseLogs?.[today] || {};
    const onceki = getDoseLog(medicine, today, time);
    const nowIso = new Date().toISOString();
    let yeniLog = { status: action, updatedAt: nowIso };

    if (action === "taken") {
      yeniLog.takenAt = nowIso;
    } else if (action === "snoozed") {
      yeniLog = {
        ...onceki,
        status: "snoozed",
        snoozedAt: nowIso,
        snoozeUntil: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
        updatedAt: nowIso,
      };
    } else if (action === "skipped") {
      yeniLog.skippedAt = nowIso;
    }

    await saglikKaydiGuncelleService({
      familyId,
      recordId: medicine.id,
      payload: {
        doseLogs: {
          ...(medicine.doseLogs || {}),
          [today]: {
            ...mevcutGun,
            [time]: yeniLog,
          },
        },
        updatedAt: nowIso,
      },
    });
  };

  const deleteRecord = async (record) => {
    if (!canManage) return false;
    if (!await appConfirm(t("health.confirmDelete"))) return false;
    await saglikKaydiSilService({ familyId, recordId: record.id });
    return true;
  };

  const updateRecord = async (record, form) => {
    if (!canManage) return;

    let data = { ...form };
    delete data.dosya;

    if (record.type === "medicine") {
      const times = normalizeMedicineTimes(form.timesText || "");
      if (times.length === 0) {
        appAlert(t("health.invalidMedTime"));
        return;
      }

      data = {
        name: form.name || t("health.card.medicine"),
        dose: form.dose || "",
        note: form.note || "",
        times,
        startDate: form.startDate || record.startDate || bugunKey(),
        endDate: form.endDate || "",
        doseLogs: record.doseLogs || {},
        takenDates: record.takenDates || {},
        reminderEnabled: record.reminderEnabled !== false,
      };
    }


    if (record.type === "appointment") {
      data = {
        doctor: form.doctor || hl(lang, "Doktor / Hastane belirtilmedi"),
        branch: form.branch || t("health.appointment.default"),
        appointmentType: form.appointmentType || (lang === "en" ? "Examination" : "Muayene"),
        date: form.date || record.date || bugunKey(),
        time: form.time || "",
        location: form.location || "",
        note: form.note || "",
        addToCalendar: form.addToCalendar !== false,
        createTask: !!form.createTask,
      };
    }

    if (record.type === "measurement") {
      data = { measurementType: form.measurementType, systolic: form.systolic || "", diastolic: form.diastolic || "", value: form.measurementType === "Tansiyon" ? `${form.systolic || "-"}/${form.diastolic || "-"}` : normalizeBodyMeasurementInput(form.measurementType, form.value), unit: bodyMeasurementUnit(form.measurementType) || form.unit || "", context: form.context || "", date: form.date || bugunKey(), note: form.note || "" };
    }
    if (record.type === "allergy") data = { name: form.name || t("health.card.allergy"), allergyType: form.allergyType || hl(lang, "Diğer"), level: form.level || hl(lang, "Belirtilmedi"), symptoms: form.symptoms || "", action: form.action || "", avoid: form.avoid || "", note: form.note || form.avoid || "" };
    if (record.type === "condition") data = { title: form.title || t("health.card.condition"), diagnosisDate: form.diagnosisDate || "", doctor: form.doctor || "", followUp: form.followUp || "", medicines: form.medicines || "", note: form.note || form.medicines || "", active: form.active !== false };
    if (record.type === "vaccine") data = { title: form.title || t("health.card.vaccine"), doseNo: form.doseNo || "", plannedDate: form.plannedDate || bugunKey(), done: !!form.done, doneDate: form.doneDate || "", nextDoseDate: form.nextDoseDate || "", note: form.note || "" };
    if (record.type === "emergency") data = { title: form.contactName || form.title || t("health.card.emergency"), bloodType: form.bloodType || "", contactName: form.contactName || form.title || "", relation: form.relation || "", phone: form.phone || "", criticalAllergies: form.criticalAllergies || "", criticalConditions: form.criticalConditions || "", criticalMedicines: form.criticalMedicines || "", note: form.note || form.criticalConditions || "" };
    if (record.type === "note") data = { title: form.title || t("health.card.note"), category: form.category || hl(lang, "Genel"), date: form.date || bugunKey(), note: form.note || "" };
    if (record.type === "report") data = { title: form.title || t("health.defaultReport"), reportType: form.reportType || hl(lang, "Diğer"), date: form.date || bugunKey(), institution: form.institution || "", doctor: form.doctor || "", resultSummary: form.resultSummary || "", note: form.note || form.resultSummary || "" };

    await saglikKaydiGuncelleService({
      familyId,
      recordId: record.id,
      payload: {
        ...data,
        memberUid: uye.uid,
        memberName: `${uye.ad} ${uye.soyad}`,
        calendarColor: uye.calendarColor || "#8b5cf6",
        memberColor: uye.calendarColor || "#8b5cf6",
        ekleyen: (() => { const me = uyeler.find(u => u.uid === user?.uid); return (me ? `${me.ad || ""} ${me.soyad || ""}`.trim() : "") || user?.displayName || t("health.userFallback"); })(),
        ekleyenUid: user?.uid,
        updatedAt: new Date().toISOString(),
      },
    });

    setEditingRecord(null);
  };

  const cardSummary = (card) => {
    const list = memberRecords.filter((r) => r.type === card.id);

    if (card.id === "medicine") {
      const active = list.length;
      const toplamDoz = list.reduce((sum, r) => sum + getMedicineTodayStats(r).total, 0);
      const alinanDoz = list.reduce((sum, r) => sum + getMedicineTodayStats(r).taken, 0);
      const siradaki = list
        .flatMap((r) => normalizeMedicineTimes(r.times || []).map((time) => ({ r, time, info: doseStatusInfo(r, time) })))
        .filter((x) => ["pending", "upcoming", "due", "snoozed"].includes(x.info.status))
        .sort((a, b) => getDoseDate(a.time) - getDoseDate(b.time))[0];

      const nextLabel = siradaki
        ? `${siradaki.time} · ${siradaki.r.name || t("health.card.medicine")}`
        : active > 0 ? t("health.summary.todayDosesDone") : t("health.summary.noMedicine");

      return [`${active} ${t("health.summary.activeMedicine")} · ${alinanDoz}/${toplamDoz}`, nextLabel];
    }

    if (card.id === "appointment") {
      const next = sortByDateAsc(list.filter((x) => !x.date || x.date >= bugunKey()))[0];
      return [`${list.length} ${t("health.card.appointment")}`, next?.date ? `${formatDateTR(next.date, lang)} ${next.time || ""}` : t("health.record.noAppointment")];
    }

    if (card.id === "measurement") {
      // Önce date'e göre sırala, aynı tarihteki kayıtlarda createdAt'a göre ikincil
      // sıralama uygula — böylece aynı gün eklenen birden fazla ölçümde son eklenen görünür.
      const last = [...list].sort((a, b) => {
        const dateCmp = String(a.date || "").localeCompare(String(b.date || ""));
        if (dateCmp !== 0) return dateCmp;
        return String(a.createdAt || "").localeCompare(String(b.createdAt || ""));
      }).reverse()[0];
      return [`${list.length} ${t("health.record.recordCount")}`, last ? getRecordSub(last, t, lang) : t("health.record.noMeasurement")];
    }

    if (card.id === "emergency") {
      return [`${list.length} ${t("health.record.recordCount")}`, list[0]?.bloodType ? `${list[0].bloodType}` : t("health.summary.emergencyCard")];
    }

    if (card.id === "report") {
      const toplam = list.length + saglikBelgeleri.length;
      return [`${toplam} ${t("health.record.recordCount")}`, saglikBelgeleri[0]?.ad || list[0]?.title || t("health.summary.noData")];
    }

    if (card.id === "summary") {
      // Bölüm 13: Sağlık özetini otomatik oluştur
      const medicineCount = memberRecords.filter(r => r.type === "medicine").length;
      const appointmentList = memberRecords.filter(r => r.type === "appointment");
      const nextAppt = appointmentList.sort((a,b) => (a.date||"").localeCompare(b.date||""))[0];
      const allergyCount = memberRecords.filter(r => r.type === "allergy").length;
      const conditionCount = memberRecords.filter(r => r.type === "condition").length;
      const emergencyList = memberRecords.filter(r => r.type === "emergency");
      
      const parts = [];
      if (medicineCount > 0) parts.push(`💊 ${medicineCount} ${t("health.summary.activeMedicine")}`);
      if (nextAppt) parts.push(`📅 ${t("health.summary.appointment")} ${nextAppt.date}`);
      if (allergyCount > 0) parts.push(`⚠️ ${allergyCount} ${t("health.card.allergy")}`);
      if (conditionCount > 0) parts.push(`🫀 ${conditionCount} ${t("health.summary.chronic")}`);
      if (emergencyList.length > 0) parts.push(`🚨 ${t("health.summary.emergencyAvail")}`);
      
      return [
        `${memberRecords.length} ${t("health.record.recordCount")}`,
        parts.length > 0 ? parts[0] : t("health.summary.noData")
      ];
    }

    if (card.id === "allergy") {
      const critical = list.filter((x) => ["Ciddi", "Acil"].includes(x.level)).length;
      return [`${list.length} ${t("health.card.allergy")}`, critical ? `🚨 ${critical} ${t("health.critical")}` : (list[0] ? getRecordTitle(list[0], t) : t("health.summary.noData"))];
    }

    if (card.id === "condition") {
      const active = list.filter((x) => x.active !== false).length;
      return [`${active}/${list.length} ${t("health.status.active")}`, list[0] ? getRecordTitle(list[0], t) : t("health.summary.noData")];
    }

    if (card.id === "vaccine") {
      const pending = list.filter((x) => !x.done).length;
      return [`${pending} ${t("common.pending")}`, list[0] ? getRecordTitle(list[0], t) : t("health.summary.noData")];
    }

    return [`${list.length} ${t("health.record.recordCount")}`, list[0] ? getRecordTitle(list[0], t) : t("health.summary.noData")];
  };

  const healthCardRows = [];
  for (let i = 0; i < cardsWithOzel.length; i += 2) {
    healthCardRows.push(cardsWithOzel.slice(i, i + 2));
  }

  return (
    <div ref={detailPageRef} className={`pageContent healthDetailPageWrapper ${transitionClass}`}> 
      <div className="belgeAltSayfaHeader">
        <button className="belgeGeriBtn" onClick={onGeri}>
          ‹
        </button>

        <div className="memberAvatar" style={{ flexShrink: 0 }}>
          <AvatarImg
            avatar={uye.avatar || null}
            renk={uye.calendarColor || "#ef476f"}
            initials={`${uye.ad?.[0]||""}${uye.soyad?.[0]||""}`}
            size={38}
            glow={false}
            radius="50%"
          
                      uid={uye.uid || uye.id}
                      isOnline={uye.isOnline === true || uye.online === true}
                    />
        </div>

        <div>
          <div className="belgeAltSayfaBaslik">
            {uye.ad} {uye.soyad}
          </div>
          <div className="belgeAltSayfaAlt">❤️ {hl(lang, "Sağlık Merkezi")} · {memberRecords.length} {hl(lang, "kayıt")}</div>
        </div>
      </div>

      <div className="healthCardGrid">
        {healthCardRows.map((row, rowIndex) => {
          const rowHasOpen = row.some((c) => activeCard === c.id);

          return (
            <div key={rowIndex} className={`healthCardRow ${rowHasOpen ? "hasOpen" : ""}`}>
              {row.map((card, colIndex) => {
                const isOpen = activeCard === card.id;
                const list = memberRecords.filter((r) => r.type === card.id);
                const summary = cardSummary(card);

                // Özel kart (navigasyon)
                if (card.isSpecial) {
                  const _uyeCins = (uye.cinsiyet || "").toLowerCase();
                  const _uyeRol2 = uye.rol || "";
                  const _kadinMi2 = _uyeCins === "kadın" || _uyeCins === "kadin" || _uyeCins === "female" || _uyeCins === "woman" || ["Anne","Nene","Teyze"].includes(_uyeRol2);
                  const _superAdminMi2 = user?.uid === "eRmq1zfRhKM4LH2SrGG8G1Q5tEG2" && uye.uid === user?.uid;
                  const _acabilir = _kadinMi2 || _superAdminMi2;
                  return (
                    <div
                      key={card.id}
                      className={`healthSectionCard ${activeCard === card.id ? "open" : ""} ${colIndex === 1 ? "rightCard" : "leftCard"}`}
                      style={{ "--health-color": card.color }}
                    >
                      <button
                        className="healthSectionTop"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          if (_acabilir) {
                            onOzelTakvimAc?.();
                          } else {
                            setActiveCard(p => p === card.id ? null : card.id);
                          }
                        }}
                      >
                        <div className="healthSectionIcon"><img src={card.icon} alt="" aria-hidden="true" draggable="false" /></div>
                        <div className="healthSectionText">
                          <div className="healthSectionTitle">{card.title}</div>
                          <div className="healthSectionSub">{card.subText}</div>
                          <div className="healthSectionMini">🔒 {lang === "en" ? "Private" : "Gizli"}</div>
                        </div>
                        <span className="healthSectionChevron">{activeCard === card.id && !_acabilir ? "−" : "›"}</span>
                      </button>
                      {activeCard === card.id && !_acabilir && (
                        <div style={{ padding: "14px 16px 16px", borderTop: "1px solid rgba(239,71,111,0.15)" }}>
                          <div style={{ background: "rgba(239,71,111,0.08)", border: "1px solid rgba(239,71,111,0.2)", borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 10 }}>
                            <span style={{ fontSize: 18, flexShrink: 0 }}>🔒</span>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#ef476f", marginBottom: 4 }}>
                                {lang === "en" ? "For female users only" : "Yalnızca kadın kullanıcılara özel"}
                              </div>
                              <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                                {lang === "en"
                                  ? "My Private Cycle is a personal health tracking feature exclusively designed for female users."
                                  : "Özel Takvimim, yalnızca kadın kullanıcılarımıza özel kişisel sağlık takip özelliğidir."}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div
                    key={card.id}
                    className={`healthSectionCard ${isOpen ? "open" : ""} ${
                      colIndex === 1 ? "rightCard" : "leftCard"
                    }`}
                    style={{ "--health-color": card.color }}
                  >
                    <button
                      className="healthSectionTop"
                      onClick={() => {
                        setActiveCard((p) => (p === card.id ? null : card.id));
                        setAddingType(null);
                      }}
                    >
                      <div className="healthSectionIcon"><img src={card.icon} alt="" aria-hidden="true" draggable="false" /></div>

                      <div className="healthSectionText">
                        <div className="healthSectionTitle">{card.title}</div>
                        <div className="healthSectionSub">{summary[0]}</div>
                        <div className="healthSectionMini">{summary[1]}</div>
                      </div>

                      <span className="healthSectionChevron">{isOpen ? "−" : "›"}</span>
                    </button>

                    {isOpen && (
                      <div className="healthExpandedPanel">
                        {card.id === "summary" ? (
                          <HealthSummaryDashboard uye={uye} records={memberRecords} belgeler={saglikBelgeleri} />
                        ) : list.length === 0 && !(card.id === "report" && saglikBelgeleri.length > 0) ? (
                          <div className="healthEmpty">{hl(lang, "Bu bölümde henüz kayıt yok.")}</div>
                        ) : (
                          <>
                            {list.length > 0 && (
                              <HealthRecordSlider
                                records={list}
                                color={card.color}
                                onToggle={canManage ? toggleMedicineTaken : undefined}
                                onDoseAction={canManage ? updateMedicineDose : undefined}
                                onDelete={canManage ? deleteRecord : undefined}
                                onEdit={canManage ? (r) => {
                                  setEditingRecord(r);
                                  setAddingType(null);
                                } : undefined}
                              />
                            )}

                            {card.id === "report" && saglikBelgeleri.length > 0 && (
                              <div style={{ marginTop: list.length > 0 ? "10px" : 0 }}>
                                <HealthDocumentSlider
                                  documents={saglikBelgeleri}
                                  color={card.color}
                                  user={user}
                                  yoneticiMi={yoneticiMi}
                                  canManage={canManage}
                                  belgeSil={belgeSil}
                                />
                              </div>
                            )}
                          </>
                        )}

                        {canManage && card.id !== "summary" && (
                          <>
                            {editingRecord?.type === card.id ? (
                              <HealthAddForm
                                type={card.id}
                                color={card.color}
                                initialData={editingRecord}
                                onCancel={() => setEditingRecord(null)}
                                onSave={(form) => updateRecord(editingRecord, form)}
                              />
                            ) : addingType === card.id ? (
                              <HealthAddForm
                                type={card.id}
                                color={card.color}
                                onCancel={() => setAddingType(null)}
                                onSave={(form) => addRecord(card.id, form)}
                              />
                            ) : (
                              <button
                                className="healthAddBtn"
                                style={{
                                  borderColor: `${card.color}66`,
                                  color: card.color,
                                  background: `${card.color}18`,
                                }}
                                onClick={() => setAddingType(card.id)}
                              >
                                + {t("common.add")}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HealthPage({
  familyId,
  uyeler = [],
  user,
  yoneticiMi,
  belgeler = [],
  belgeYukle,
  belgeSil,
  onHealthDetailStateChange,
  onDetailBackRequest,
  onInternalForwardTransition,
  isPremium = false,
  premiumConfig = {},
  onPremiumLimitHit,
  onOwnHealthProfileUpdated,
}) {
  const { t, lang } = useLanguage();
  const [ekran, setEkran] = useState(null);
  const [records, setRecords] = useState(() => readCachedHealthRecords(familyId));
  const iosAppleHealth = useMemo(() => isIOSPlatform(), []);
  const [appleHealthStatus, setAppleHealthStatus] = useState(() => ({
    available: iosAppleHealth,
    shouldRequest: true,
    status: iosAppleHealth ? "unknown" : "unavailable",
  }));
  const [appleHealthData, setAppleHealthData] = useState(APPLE_HEALTH_EMPTY_DATA);
  const [appleHealthLoading, setAppleHealthLoading] = useState(iosAppleHealth);
  const [appleHealthError, setAppleHealthError] = useState(null);
  const healthMainPageRef = useRef(null);
  const healthOpenAnimatingRef = useRef(false);

  const loadAppleHealth = useCallback(async ({ forceRead = false, quiet = false } = {}) => {
    if (!iosAppleHealth) return;
    if (!quiet) setAppleHealthLoading(true);
    setAppleHealthError(null);

    try {
      const nextStatus = await getHealthKitStatus();
      setAppleHealthStatus(nextStatus);
      if (nextStatus?.available && (forceRead || nextStatus.shouldRequest === false)) {
        const nextData = await readHealthKitSummary();
        setAppleHealthData({ ...APPLE_HEALTH_EMPTY_DATA, ...nextData });
      }
    } catch (error) {
      setAppleHealthError(error);
      // Native köprü uygulamanın ilk karesinde henüz bağlanmamış olabilir.
      // Bu geçici durumda kart mevcut düzenini korur ve sonraki denemeyi bekler.
      if (error?.code !== "HEALTHKIT_BRIDGE_NOT_READY") {
        console.warn("[HealthPage] Apple Health verileri okunamadı:", error?.message || error);
      }
    } finally {
      setAppleHealthLoading(false);
    }
  }, [iosAppleHealth]);

  const authorizeAppleHealth = useCallback(async () => {
    if (!iosAppleHealth) return;
    setAppleHealthError(null);
    try {
      await requestHealthKitAuthorization();
      await loadAppleHealth({ forceRead: true });
    } catch (error) {
      setAppleHealthError(error);
      throw error;
    }
  }, [iosAppleHealth, loadAppleHealth]);

  useEffect(() => {
    if (!iosAppleHealth) return undefined;

    loadAppleHealth();
    const bridgeRetry = window.setTimeout(() => loadAppleHealth({ quiet: true }), 1400);
    const unsubscribe = subscribeHealthKitRefresh(() => loadAppleHealth({ quiet: true }));
    const onVisible = () => {
      if (document.visibilityState === "visible") loadAppleHealth({ quiet: true });
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearTimeout(bridgeRetry);
      unsubscribe();
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [iosAppleHealth, loadAppleHealth]);

  // ── Push bildirim deep link: Özel Takvimim ekranını dışarıdan aç ─────────
  // useNativePush.js'teki triggerOzelTakvimIfNeeded() bu fonksiyonu polling ile çağırır.
  // Cycle/özel takvim bildirimlerine dokunulduğunda saglik sayfası açılır,
  // ardından bu bridge ile OzelTakvimim ekranına geçilir.
  useEffect(() => {
    window.__healthOpenOzelTakvim = () => {
      const commit = () => {
        window.scrollTo({ top: 0, behavior: "auto" });
        setEkran({ tip: "ozelTakvim" });
      };
      if (typeof onInternalForwardTransition === "function") {
        onInternalForwardTransition(commit);
      } else {
        commit();
      }
    };
    return () => {
      window.__healthOpenOzelTakvim = null;
    };
  }, [onInternalForwardTransition]); // eslint-disable-line


  useEffect(() => {
    let cancelled = false;

    const cachedRecords = readCachedHealthRecords(familyId);
    setRecords(cachedRecords);

    const loadHealthRecords = async () => {
      if (!familyId) {
        setRecords([]);
        return;
      }

      try {
        const list = await saglikKayitlariGetirService(familyId);
        if (!cancelled) {
          writeCachedHealthRecords(familyId, list);
          setRecords(list);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("[HealthPage] Sağlık kayıtları backend'den alınamadı:", err);
          const latestCachedRecords = readCachedHealthRecords(familyId);
          setRecords(latestCachedRecords);
        }
      }
    };

    loadHealthRecords();

    const onHealthDataChanged = (event) => {
      const detail = event?.detail || {};
      if (String(detail.familyId || "") !== String(familyId || "")) return;
      if (detail.resource !== "health") return;
      loadHealthRecords();
    };

    window.addEventListener("fp:restDataChanged", onHealthDataChanged);
    window.addEventListener("fp:dataChanged", onHealthDataChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("fp:restDataChanged", onHealthDataChanged);
      window.removeEventListener("fp:dataChanged", onHealthDataChanged);
    };
  }, [familyId]);

  const siraliUyeler = useMemo(() => {
    const temel = uyelerSirala(uyeler);
    return [
      ...temel.filter(u => u.uid === user?.uid),
      ...temel.filter(u => u.uid !== user?.uid),
    ];
  }, [uyeler, user?.uid]);
  const mevcutUye = useMemo(() => siraliUyeler.find((u) => u.uid === user?.uid), [siraliUyeler, user?.uid]);

  const healthDetailBack = useMemo(() => {
    if (ekran?.tip !== "uye" && ekran?.tip !== "appleHealth") return null;

    return () => {
      setEkran(null);
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "auto" });
      });
    };
  }, [ekran]);

  useEffect(() => {
    if ((ekran?.tip === "uye" || ekran?.tip === "appleHealth") && healthDetailBack) {
      // Kullanıcı kartı açık: commit back = karta geri dön
      document.body.classList.add("health-detail-open");
      onHealthDetailStateChange?.(true);
      const fn = healthDetailBack;
      window.__healthDetailCommitBack = fn;
      return () => {
        document.body.classList.remove("health-detail-open");
        // Sadece BU effect'in koyduğu fn'i sil
        if (window.__healthDetailCommitBack === fn) delete window.__healthDetailCommitBack;
      };
    }

    if (ekran?.tip === "ozelTakvim") {
      // OzelTakvimim açık: commit back = sağlık listesine dön
      document.body.classList.add("health-detail-open");
      onHealthDetailStateChange?.(true);
      const fn = () => setEkran(null);
      window.__healthDetailCommitBack = fn;
      return () => {
        document.body.classList.remove("health-detail-open");
        // Sadece BU effect'in koyduğu fn'i sil
        if (window.__healthDetailCommitBack === fn) delete window.__healthDetailCommitBack;
      };
    }

    // ekran = null (sağlık listesi veya BG render):
    // __healthDetailCommitBack'e DOKUNMA — bir önceki effect'in cleanup'ı siler
    document.body.classList.remove("health-detail-open");
    return () => { document.body.classList.remove("health-detail-open"); };
  }, [ekran, healthDetailBack, onHealthDetailStateChange]);


  // Sayfa tamamen unmount olduğunda (navigateTo ile ayrılma) healthDetailAcik sıfırlanır
  useEffect(() => {
    return () => {
      onHealthDetailStateChange?.(false);
    };
  }, [onHealthDetailStateChange]);

  if (ekran?.tip === "appleHealth") {
    return (
      <AppleHealthSummaryPage
        status={appleHealthStatus}
        data={appleHealthData}
        loading={appleHealthLoading}
        error={appleHealthError}
        selectedMetric={ekran.metric}
        onBack={onDetailBackRequest || healthDetailBack || (() => setEkran(null))}
        onAuthorize={authorizeAppleHealth}
        onRefresh={() => loadAppleHealth({ forceRead: true })}
        onSettings={async () => {
          try { await openHealthKitSettings(); } catch (error) { setAppleHealthError(error); }
        }}
      />
    );
  }

  if (ekran?.tip === "uye") {
    return (
      <HealthDetailPage
        uye={ekran.uye}
        records={records}
        user={user}
        familyId={familyId}
        yoneticiMi={yoneticiMi}
        uyeler={uyeler}
        belgeler={belgeler}
        belgeYukle={belgeYukle}
        belgeSil={belgeSil}
        onGeri={onDetailBackRequest || healthDetailBack || (() => setEkran(null))}
        transitionClass=""
        isPremium={isPremium}
        premiumConfig={premiumConfig}
        onPremiumLimitHit={onPremiumLimitHit}
        onOzelTakvimAc={() => {
          const commit = () => {
            window.scrollTo({ top: 0, behavior: "auto" });
            setEkran({ tip: "ozelTakvim", uye: ekran?.uye });
          };
          if (typeof onInternalForwardTransition === "function") {
            onInternalForwardTransition(commit);
          } else {
            commit();
          }
        }}
      />
    );
  }

  // Özel Takvimim ekranı
  if (ekran?.tip === "ozelTakvim") {
    const backToDetail = () => {
      window.scrollTo({ top: 0, behavior: "auto" });
      if (ekran.uye) {
        setEkran({ tip: "uye", uye: ekran.uye });
      } else {
        // Push deep link'i kullanıcı kartı bağlamı olmadan Özel Takvim'i açar.
        // Bu durumda tanımsız bir kullanıcı detayına gitmek boş ekran üretir;
        // güvenli geri hedefi Sağlık ana listesidir.
        setEkran(null);
      }
    };
    return (
      <div className="pageContent" style={{ padding: 0, minHeight: "100dvh" }}>
        <OzelTakvimim onGeri={backToDetail} />
      </div>
    );
  }

  // Anne/Baba rolleri çocukların sağlık verilerine erişebilir
  const ebeveynMi = mevcutUye?.rol === "Anne" || mevcutUye?.rol === "Baba";
  const kartaErisebilirMi = (u) => {
    if (u.uid === user?.uid) return true;  // Kendi kartı
    if (user?.uid === "eRmq1zfRhKM4LH2SrGG8G1Q5tEG2") return true;  // SuperAdmin
    if (ebeveynMi && (u.rol === "Çocuk" || u.rol === "Bebek")) return true;  // Ebeveyn → çocuk
    return false;  // Diğerleri birbirinin kartına giremez
  };

  const uyeDetayAc = (u) => {
    if (!kartaErisebilirMi(u) || healthOpenAnimatingRef.current) return;

    const commit = () => {
      window.scrollTo({ top: 0, behavior: "auto" });
      setEkran({ tip: "uye", uye: u });
    };

    if (typeof onInternalForwardTransition !== "function") {
      commit();
      return;
    }

    healthOpenAnimatingRef.current = true;
    onInternalForwardTransition(commit);
    setTimeout(() => {
      healthOpenAnimatingRef.current = false;
    }, 260);
  };

  const appleHealthDetayAc = (metric) => {
    if (!iosAppleHealth || healthOpenAnimatingRef.current) return;

    const commit = () => {
      window.scrollTo({ top: 0, behavior: "auto" });
      setEkran({ tip: "appleHealth", metric });
    };

    if (typeof onInternalForwardTransition !== "function") {
      commit();
      return;
    }

    healthOpenAnimatingRef.current = true;
    onInternalForwardTransition(commit);
    setTimeout(() => {
      healthOpenAnimatingRef.current = false;
    }, 260);
  };

  return (
    <div ref={healthMainPageRef} className="pageContent healthPageRoot">
      <div className="belgeAnaSayfaBaslik">{hl(lang, "Aile Sağlık Kartları")}</div>

      <MyHealthProfile
        uye={mevcutUye}
        user={user}
        familyId={familyId}
        onOwnHealthProfileUpdated={onOwnHealthProfileUpdated}
        showAppleHealth={iosAppleHealth}
        appleHealthStatus={appleHealthStatus}
        appleHealthData={appleHealthData}
        appleHealthLoading={appleHealthLoading}
        onAppleHealthOpen={appleHealthDetayAc}
      />

      <div className="statsBand" style={{ marginBottom: "16px" }}>
        <div className="statsBandItem">
          <span className="statsBandNum">{records.length}</span>
          <span className="statsBandLbl">{hl(lang, "Sağlık Kaydı")}</span>
        </div>

        <div className="statsBandDivider" />

        <div className="statsBandItem">
          <span className="statsBandNum red">{records.filter((r) => r.type === "medicine").length}</span>
          <span className="statsBandLbl">{t("health.card.medicine")}</span>
        </div>

        <div className="statsBandDivider" />

        <div className="statsBandItem">
          <span className="statsBandNum yellow">{records.filter((r) => r.type === "appointment").length}</span>
          <span className="statsBandLbl">{t("health.card.appointment")}</span>
        </div>
      </div>


      <div className="belgeUyeGrid">
        {siraliUyeler.map((u) => {
          const renk = u.calendarColor || "#ef476f";
          const uyeRecords = records.filter((r) => r.memberUid === u.uid);
          const medicineCount = uyeRecords.filter((r) => r.type === "medicine").length;
          const emergencyCount = uyeRecords.filter((r) => r.type === "emergency").length;
          const kendiMi = u.uid === user?.uid;

          return (
            <button
              key={u.uid}
              className="belgeUyeKarti"
              style={{
                borderColor: `${renk}44`,
                background: `${renk}0d`,
                opacity: kartaErisebilirMi(u) ? 1 : 0.5,
                cursor: kartaErisebilirMi(u) ? "pointer" : "not-allowed",
              }}
              onClick={() => uyeDetayAc(u)}
            >
              <div className="belgeUyeKartiAvatar" style={{ overflow: "hidden" }}>
                <AvatarImg
                  avatar={u.avatar || null}
                  renk={renk}
                  initials={`${u.ad?.[0]||""}${u.soyad?.[0]||""}`}
                  size={50}
                  glow={false}
                  radius="50%"
                
                      uid={u.uid || u.id}
                      isOnline={u.isOnline === true || u.online === true}
                    />
              </div>

              <div className="belgeUyeKartiAd">
                {u.ad}
                {kendiMi && <span className="belgeUyeKartiSen"> ({hl(lang, "Ben")})</span>}
              </div>

              <div className="belgeUyeKartiRol">{hl(lang, u.rol)}</div>

              <div className="belgeUyeKartiSayi" style={{ color: renk }}>
                {uyeRecords.length} {hl(lang, "sağlık kaydı")}
              </div>

              <div className="healthUserMiniInfo">💊 {medicineCount} · 🚨 {emergencyCount}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default HealthPage;
